import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Field, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Textarea } from "@/components/ui/textarea";
import { Toaster } from "@/components/ui/sonner";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import whiteLogo from "@/assets/images/imautotech-horizontal-logo.svg";
import aboutUsImage from "@/assets/images/imautotech-about-us.webp";
import servicesHeroImage from "@/assets/images/imautotech-services.webp";
import diagnosticsImage from "@/assets/images/services/imautotech-diagnostics.webp";
import egrSystemImage from "@/assets/images/services/egr-system.webp";
import dpfSystemImage from "@/assets/images/services/dpf-system.webp";
import scrAdblueSystemImage from "@/assets/images/services/scr-adblue-system.webp";
import emissionsSystemsImage from "@/assets/images/services/emissions-systems.webp";
import { ArrowLeft, ArrowUpRight, CalendarCheck, Check, ChevronDown, Clock3, Loader2, Mail, MapPin, Menu, MessageCircle, Phone, SearchCheck, ShieldCheck } from "lucide-react";
import { Children, isValidElement, useEffect, useRef, useState, type FormEvent, type ImgHTMLAttributes, type ReactNode } from "react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { applySeoMetadata, getSeoPage } from "@/seo";
import { trackGenerateLead } from "@/analytics";
import "./index.css";

const images = {
  hero: "https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&w=2200&q=85",
  about: aboutUsImage,
  servicesHero: servicesHeroImage,
  diagnostics: diagnosticsImage,
  maintenance: "https://images.unsplash.com/photo-1487754180451-c456f719a1fc?auto=format&fit=crop&w=1000&q=80",
  brakes: "https://images.unsplash.com/photo-1760317890314-e964ffd7e6a6?auto=format&fit=crop&w=1200&q=85",
  consultation: "https://images.unsplash.com/photo-1643700973089-baa86a1ab9ee?auto=format&fit=crop&w=1200&q=85",
  egr: egrSystemImage,
  dpf: dpfSystemImage,
  scrAdblue: scrAdblueSystemImage,
  emissions: emissionsSystemsImage,
  tires: "https://images.unsplash.com/photo-1645445522156-9ac06bc7a767?auto=format&fit=crop&w=1600&q=85",
} as const;

const phoneNumbers = [
  { value: "+421 949 220 152", href: "tel:+421949220152" },
  { value: "+421 918 414 898", href: "tel:+421918414898" },
] as const;

const aboutBenefitIcons = [SearchCheck, MessageCircle, ShieldCheck, CalendarCheck] as const;

const content = {
  sk: {
    logoAlt: "IM Autotech",
    labels: { services: "Služby", about: "Prečo my", contact: "Kontakt", home: "Úvod", book: "Objednať termín", call: "Zavolať teraz", allServices: "Všetky služby", learnMore: "Spoznajte nás", location: "Kontakt a lokalita", hours: "Otváracie hodiny" },
    eyebrow: "Diagnostika, servis a opravy áut", headline: "Postaráme sa o vaše auto.", text: "Diagnostika auta, pravidelná údržba a opravy s presným určením chyby. Najprv nájdeme príčinu, potom spolu vyberieme správne riešenie.",
    quick: [["Telefón", phoneNumbers[0].value, phoneNumbers[0].href, Phone], ["Telefón", phoneNumbers[1].value, phoneNumbers[1].href, Phone], ["Otváracie hodiny", "Po–Pi 08:00–17:00", "#hodiny", Clock3], ["Nájdete nás", "Coburgova 2263/84,\nTrnava", "https://maps.app.goo.gl/k6JvXCn5b1G5m52y7", MapPin]],
    services: { kicker: "Naše služby", title: "S čím vám pomôžeme", text: "Od bežnej údržby po problém, ktorý sa ozve cestou do práce. Vysvetlíme, čo kontrolujeme a prečo.", items: [
      { number: "01", slug: "diagnostika", title: "Autodiagnostika", text: "Chybové kódy, elektronika a hľadanie skutočnej príčiny poruchy.", detailTitle: "Diagnostika auta, ktorá hľadá príčinu.", detailText: "Načítame chybové kódy, skontrolujeme živé dáta a zmeriame súvisiace systémy. Výsledkom je jasné vysvetlenie poruchy a odporúčaný ďalší postup.", image: images.diagnostics, checks: ["Elektronické systémy a chybové kódy", "Meranie a kontrola živých dát", "Jasné vysvetlenie príčiny a ďalšieho postupu"] },
      { number: "02", slug: "pravidelny-servis", title: "Výmena oleja a pravidelný servis", text: "Olej, filtre, kvapaliny a kontrola dielov, od ktorých závisí spoľahlivosť.", detailTitle: "Údržba podľa auta a spôsobu jazdy.", detailText: "Servisný rozsah nastavíme podľa odporúčaní výrobcu, veku vozidla a jeho používania. Vymeníme potrebné náplne a filtre a upozorníme na diely, ktoré si vyžadujú pozornosť.", image: images.maintenance, checks: ["Výmena oleja, filtrov a prevádzkových kvapalín", "Kontrola opotrebovania bezpečnostných dielov", "Servisný interval podľa vozidla a spôsobu jazdy"] },
      { number: "03", slug: "brzdy-a-podvozok", title: "Oprava bŕzd a podvozku", text: "Bezpečnostne dôležité opravy bŕzd, tlmičov, čapov a ramien.", detailTitle: "Kontrola bezpečnostných dielov bez kompromisov.", detailText: "Preveríme brzdy, podvozok, vôle aj zdroj neželaných zvukov. Pred opravou dostanete prehľad o stave dielov a odporúčanom poradí zásahov.", image: images.brakes, checks: ["Brzdové kotúče, platničky a kvapalina", "Tlmiče, ramená, čapy a uloženia", "Kontrola vôle, hluku a stability"] },
      { number: "04", slug: "diagnostika-oprava-egr", title: "Diagnostika a oprava EGR", text: "Kontrola a oprava problémov súvisiacich so systémom EGR.", detailTitle: "EGR systém pod kontrolou.", detailText: "Overíme stav systému EGR, súvisiace snímače a chybové hlásenia. Výsledkom je jasné vyhodnotenie stavu a odporúčanie ďalšieho postupu opravy.", image: images.egr, checks: ["Kontrola systému EGR a súvisiacich snímačov", "Diagnostika chybových kódov a živých dát", "Odporúčanie ďalšieho postupu podľa stavu vozidla"] },
      { number: "05", slug: "diagnostika-servis-dpf", title: "Diagnostika a servis DPF", text: "Kontrola a servis problémov s filtrom pevných častíc.", detailTitle: "DPF bez zbytočného hádania.", detailText: "Skontrolujeme údaje zo systému DPF, regeneráciu aj súvisiace snímače. Pomôžeme vyhodnotiť stav filtra a zvoliť rozumný ďalší krok.", image: images.dpf, checks: ["Kontrola zanesenia a diferenčného tlaku", "Diagnostika regenerácie a súvisiacich snímačov", "Vyhodnotenie stavu filtra a ďalšieho postupu"] },
      { number: "06", slug: "diagnostika-oprava-adblue", title: "Diagnostika a oprava SCR / AdBlue", text: "Diagnostika systému SCR, vstrekovania AdBlue a súvisiacich chýb.", detailTitle: "SCR a AdBlue s jasným vysvetlením.", detailText: "Preveríme systém SCR, dávkovanie AdBlue a súvisiace chybové hlásenia. Vysvetlíme, čo problém spôsobuje a aká oprava dáva zmysel.", image: images.scrAdblue, checks: ["Diagnostika systému SCR a AdBlue", "Kontrola čerpadla, vstrekovania a snímačov", "Vyhodnotenie chybových hlásení"] },
      { number: "07", slug: "diagnostika-emisnych-systemov", title: "Diagnostika emisných systémov", text: "Kontrola emisných systémov podľa stavu a použitia vozidla.", detailTitle: "Emisné systémy podľa skutočného stavu auta.", detailText: "Pozrieme sa na dostupné dáta, chybové hlásenia a súvislosti medzi emisnými systémami. Navrhneme vhodný postup opravy podľa konkrétneho vozidla.", image: images.emissions, checks: ["Kontrola emisných systémov vozidla", "Vyhodnotenie súvisiacich dát a chýb", "Návrh riešenia podľa stavu vozidla"] },
      { number: "08", slug: "pneuservis", title: "Pneuservis", text: "Prezutie, vyváženie kolies a kontrola pneumatík pred sezónou aj počas nej.", detailTitle: "Pneumatiky pripravené na ďalšiu sezónu.", detailText: "Prezujeme pneumatiky, vyvážime kolesá a skontrolujeme stav dezénu, tlak aj viditeľné poškodenia. Termín si dohodneme vopred, aby ste zbytočne nečakali.", image: images.tires, checks: ["Prezutie pneumatík a kolies", "Vyváženie kolies a kontrola tlaku", "Kontrola dezénu a viditeľného poškodenia"] },
      { number: "09", slug: "konzultacia", title: "Konzultácia poruchy vozidla", text: "Neviete presne, čo sa s autom deje? Začneme krátkou konzultáciou chyby vozidla.", detailTitle: "Najprv si ujasníme, čo auto potrebuje.", detailText: "Prejdeme príznaky, históriu vozidla aj vaše očakávania. Navrhneme vhodnú kontrolu alebo diagnostiku a dohodneme rozsah aj termín ďalšieho postupu.", image: images.consultation, checks: ["Krátke zhodnotenie príznakov", "Odporúčanie vhodnej diagnostiky alebo kontroly", "Dohoda o rozsahu, termíne a ďalšom postupe"] },
    ] },
    serviceDetail: { back: "Všetky služby", included: "Rozsah služby", cta: "Dohodnúť túto službu" },
    about: {
      kicker: "Prečo IM Autotech",
      title: "Najprv diagnostika. Potom rozhodnutie.",
      text: "Žiadne tlačenie do zbytočných opráv. Stav auta, možnosti a cenu si prejdeme skôr, než sa pustíme do práce.",
      points: ["Jasné vysvetlenie pred opravou", "Rozumné odporúčania podľa stavu auta", "Termín a rozsah práce dohodnutý vopred"],
      page: {
        kicker: "O nás",
        title: "Autoservis, ktorému môžete rozumieť.",
        text: "IM Autotech je autoservis v Trnave zameraný na presnú diagnostiku, poctivé opravy a otvorenú komunikáciu. Vaše auto najprv dôkladne preveríme, vysvetlíme možnosti a až potom sa spolu dohodneme na riešení.",
        cta: "Dohodnúť termín",
        imageAlt: "Mechanik pri kontrole vozidla v dielni",
        whyKicker: "Prečo si vybrať nás",
        whyTitle: "Dobré rozhodnutie začína správnou diagnózou.",
        whyText: "K autu pristupujeme systematicky. Nehľadáme najrýchlejšiu výmenu dielu, ale riešenie, ktoré dáva zmysel technicky aj finančne.",
        benefits: [
          ["Presná diagnostika", "Hľadáme skutočnú príčinu problému, nie iba spôsob, ako zhasnúť kontrolku."],
          ["Zrozumiteľná komunikácia", "Stav auta aj možnosti opravy vysvetlíme jasne, bez zbytočného technického žargónu."],
          ["Férové odporúčania", "Navrhneme riešenie podľa stavu a používania vozidla, nie podľa ceny najdrahšieho dielu."],
          ["Dohodnutý postup", "Rozsah práce, termín aj ďalšie kroky si potvrdíme skôr, než začneme s opravou."],
        ],
        faqKicker: "Časté otázky",
        faqTitle: "Čo je dobré vedieť pred návštevou.",
        faq: [
          ["Musím sa na servis objednať vopred?", "Áno, odporúčame dohodnúť si termín telefonicky alebo cez kontaktný formulár. Vieme si tak vyhradiť potrebný čas a podľa dohody pripraviť diely."],
          ["Viete zistiť príčinu rozsvietenej kontrolky?", "Áno. Pri diagnostike nepracujeme iba s chybovým kódom, ale kontrolujeme aj živé dáta a súvisiace systémy, aby sme našli pravdepodobnú príčinu problému."],
          ["Začnete s opravou hneď po diagnostike?", "Nie bez dohody. Najprv vám vysvetlíme zistený stav, odporúčaný postup a rozsah opravy. Do práce sa pustíme až po vašom potvrdení."],
          ["Aké vozidlá servisujete?", "Pomáhame s osobnými vozidlami rôznych značiek. Pri objednaní nám povedzte značku, model, rok výroby a stručne opíšte problém, aby sme vedeli posúdiť vhodný postup."],
          ["Kde vás nájdem?", "Dielňu nájdete na Coburgovej 2263/84 v Trnave. Presnú polohu a odkaz na navigáciu nájdete v mape nižšie."],
        ],
      },
    },
    hours: { title: "Dohodnite si termín vopred.", text: "Krátky telefonát alebo správa nám pomôže pripraviť si čas aj potrebné diely.", rows: [["Pondelok – Piatok", "08:00 – 17:00"], ["Sobota", "Podľa dohody"], ["Nedeľa", "Zatvorené"]] },
    process: { kicker: "Ako to funguje", title: "Servis bez neistoty.", items: [["01", "Ozvite sa", "Zavolajte alebo pošlite formulár s modelom auta a stručným popisom."], ["02", "Dohodneme postup", "Upresníme termín, čo budeme kontrolovať a aké sú možnosti."], ["03", "Viete, na čom ste", "Pred opravou dostanete jasné vysvetlenie odporúčaného riešenia."]] },
    company: { title: "Fakturačné údaje", name: "IM autotech s. r. o.", ico: "57544247", dic: "2122828268", address: "Slnečná 588/32B, 919 61 Ružindol" },
    contact: { title: "Dohodnime termín servisu.", text: "Napíšte nám, čo sa s autom deje. Ozveme sa s najbližším možným termínom.", name: "Meno a priezvisko", phone: "Telefónne číslo", vehicle: "Značka a model auta", message: "Popis problému", submit: "Odoslať požiadavku", submitting: "Odosielam…", success: "Ďakujeme. Vašu požiadavku sme prijali a čoskoro sa vám ozveme.", error: "Požiadavku sa nepodarilo odoslať. Skúste to znova.", phoneLabel: "Telefón", emailLabel: "E-mailová adresa", privacyNotice: "Vaše osobné údaje spracúvame za účelom vybavenia Vašej požiadavky.", privacyPrefix: "Viac informácií nájdete v sekcii", privacyLink: "ochrana osobných údajov", addressLabel: "Adresa", phoneValues: phoneNumbers, email: "info@imautotech.sk", address: "Coburgova 2263/84, 917 02 Trnava", directions: "Otvoriť trasu" },
    footer: "Autoservis zameraný na jasnú diagnostiku, poctivú prácu a férovú komunikáciu.",
  },
} as const;

type Copy = typeof content.sk;
type ServiceItem = Copy["services"]["items"][number];
type ServiceSlug = ServiceItem["slug"];
type ServiceDetailExtra = {
  reasons: readonly string[];
  urgentNote?: string;
  steps: readonly (readonly [string, string])[];
  outcomes: readonly string[];
  faq: readonly (readonly [string, string])[];
  related: readonly ServiceSlug[];
};

const serviceDetailExtras = {
  diagnostika: {
    reasons: [
      "Na palubnej doske zostáva svietiť výstražná kontrolka.",
      "Motor stráca výkon, pracuje nepravidelne alebo sa ťažšie štartuje.",
      "Problém sa objavuje iba občas a po vypnutí auta zmizne.",
      "Elektronický systém alebo výbava vozidla nefunguje správne.",
    ],
    steps: [
      ["Prejdeme prejavy poruchy", "Spýtame sa, kedy sa problém objavuje, ako dlho trvá a čo mu predchádzalo."],
      ["Načítame a preveríme dáta", "Skontrolujeme chybové kódy, živé hodnoty a súvisiace elektronické systémy."],
      ["Porovnáme zistenia", "Podľa nameraných údajov odporučíme opravu alebo ďalšie cielené meranie."],
    ],
    outcomes: [
      "Ktorý systém vozidla si vyžaduje pozornosť.",
      "Či dostupné dáta ukazujú na konkrétnu príčinu alebo treba pokračovať v meraní.",
      "Aký ďalší krok dáva zmysel a čo si vyžaduje váš súhlas."],
    faq: [
      ["Stačí vymazať chybový kód?", "Nie. Vymazanie kódu neodstráni príčinu poruchy. Kód používame ako jeden z podkladov spolu so živými dátami a meraním."],
      ["Nájde diagnostika vždy chybný diel?", "Nie vždy pri prvej kontrole. Niektoré poruchy sú prerušované alebo majú viac možných príčin, preto môže byť potrebné ďalšie cielené meranie."],
      ["Čo si mám pred návštevou všimnúť?", "Pomôže nám informácia, kedy sa problém objavuje, či je motor studený alebo zohriaty a ktoré kontrolky sa rozsvietili."],
    ],
    related: ["konzultacia", "diagnostika-emisnych-systemov", "diagnostika-oprava-egr"],
  },
  "pravidelny-servis": {
    reasons: [
      "Vozidlo hlási blížiaci sa alebo prekročený servisný interval.",
      "Nastal termín výmeny oleja podľa servisného plánu vozidla.",
      "Servisná história auta je neúplná alebo ju nepoznáte.",
      "Chystáte sa na dlhšiu cestu a chcete preveriť základný stav vozidla.",
    ],
    steps: [
      ["Overíme vozidlo a históriu", "Rozsah servisu nastavíme podľa modelu, veku, nájazdu a spôsobu používania auta."],
      ["Vymeníme dohodnuté náplne", "Podľa dohodnutého rozsahu vymeníme olej, filtre a potrebné prevádzkové kvapaliny."],
      ["Skontrolujeme bežné opotrebovanie", "Pozrieme bezpečnostne dôležité diely a upozorníme na veci, ktoré si vyžadujú pozornosť."],
    ],
    outcomes: [
      "Čo bolo počas návštevy vymenené a skontrolované.",
      "Ktoré diely sú v poriadku a ktoré bude vhodné riešiť neskôr alebo skôr.",
      "Kedy má nasledovať ďalšia údržba podľa vozidla a jeho používania."],
    faq: [
      ["Ako často treba meniť olej?", "Riaďte sa servisným plánom výrobcu vozidla. Interval môže ovplyvniť aj spôsob používania, časté krátke jazdy alebo náročná prevádzka."],
      ["Čo ak nepoznám servisnú históriu auta?", "Začneme kontrolou dostupných údajov a základného stavu. Potom navrhneme rozumný rozsah údržby bez automatickej výmeny všetkého."],
      ["Kontrolujete pri servise aj brzdy a podvozok?", "V rámci dohodnutej kontroly vieme upozorniť na viditeľné opotrebovanie. Podrobná diagnostika bŕzd alebo podvozku môže vyžadovať samostatný rozsah."],
    ],
    related: ["brzdy-a-podvozok", "pneuservis", "diagnostika"],
  },
  "brzdy-a-podvozok": {
    reasons: [
      "Pri brzdení počujete nezvyčajné pískanie, škrípanie alebo klepanie.",
      "Pri brzdení cítite vibrácie v pedáli alebo volante.",
      "Auto sa pri jazde alebo brzdení správa neisto či ťahá do strany.",
      "Na nerovnostiach počujete klepanie alebo si všímate nerovnomerné opotrebovanie pneumatík.",
    ],
    urgentNote: "Ak sa brzdný účinok výrazne znížil, pedál sa náhle správa inak alebo svieti červená výstraha bŕzd, nepokračujte v jazde a zabezpečte bezpečný odvoz vozidla.",
    steps: [
      ["Upresníme, kedy sa prejav objavuje", "Rozlíšime, či problém súvisí s brzdením, nerovnosťami, rýchlosťou alebo smerom jazdy."],
      ["Preveríme brzdy a podvozok", "Skontrolujeme kotúče, platničky, kvapalinu, vôle a stav súvisiacich dielov podvozku."],
      ["Určíme poradie zásahov", "Bezpečnostne dôležité zistenia oddelíme od opotrebovania, ktoré možno naplánovať."],
    ],
    outcomes: [
      "Aký je stav kontrolovaných brzdových a podvozkových dielov.",
      "Ktoré zistenia treba riešiť bez odkladu a ktoré možno naplánovať.",
      "Aký rozsah opravy odporúčame pred objednaním dielov."],
    faq: [
      ["Znamená pískanie vždy opotrebované platničky?", "Nie. Hluk môže mať viac príčin, preto kontrolujeme aj kotúče, uloženie a ďalšie súvisiace diely."],
      ["Prečo volant pri brzdení vibruje?", "Vibrácie môžu súvisieť s brzdami, kolesami alebo podvozkom. Bez kontroly a merania sa konkrétna príčina nedá spoľahlivo určiť."],
      ["Musím meniť všetky diely naraz?", "Nie automaticky. Po kontrole vysvetlíme stav jednotlivých dielov a odporúčané poradie opráv."],
    ],
    related: ["pneuservis", "pravidelny-servis", "konzultacia"],
  },
  "diagnostika-oprava-egr": {
    reasons: [
      "Rozsvietila sa kontrolka motora alebo sa vracia rovnaká chyba.",
      "Motor stráca výkon alebo prejde do núdzového režimu.",
      "Chod motora je nepravidelný, najmä pri zmene zaťaženia.",
      "Diagnostika už zaznamenala chybu EGR alebo súvisiaceho snímača.",
    ],
    steps: [
      ["Preveríme chybové hlásenia", "Načítame kódy a živé dáta súvisiace so systémom EGR a chodom motora."],
      ["Skontrolujeme riadenie systému", "Pozrieme požadované a skutočné hodnoty, ovládanie a dostupné údaje zo súvisiacich snímačov."],
      ["Vyhodnotíme príčinu", "Oddelíme problém samotného EGR od chyby snímača, ovládania alebo iného súvisiaceho systému."],
    ],
    outcomes: [
      "Či dostupné údaje potvrdzujú problém v systéme EGR.",
      "Ktorá časť systému potrebuje opravu alebo ďalšiu kontrolu.",
      "Aký postup opravy dáva zmysel pre konkrétne vozidlo."],
    faq: [
      ["Znamená chyba EGR, že treba hneď meniť ventil?", "Nie. Chybové hlásenie môže súvisieť aj s ovládaním, snímačmi alebo ďalšími podmienkami, preto najprv preverujeme dáta."],
      ["Pomôže iba vymazanie kontrolky?", "Nie. Ak príčina zostane, chyba sa môže vrátiť. Vymazanie používame až ako súčasť kontroly po vykonanom zásahu."],
      ["Súvisí EGR aj s ďalšími emisnými systémami?", "Áno, údaje motora a emisné systémy sa navzájom ovplyvňujú. Preto pri diagnostike sledujeme aj súvisiace hodnoty."],
    ],
    related: ["diagnostika-servis-dpf", "diagnostika-emisnych-systemov", "diagnostika"],
  },
  "diagnostika-servis-dpf": {
    reasons: [
      "Rozsvietila sa kontrolka DPF alebo kontrolka motora.",
      "Výzva na regeneráciu sa opakuje alebo sa kontrolka po krátkom čase vráti.",
      "Vozidlo stráca výkon alebo prejde do núdzového režimu.",
      "Diagnostika hlási problém s diferenčným tlakom, teplotou alebo regeneráciou.",
    ],
    steps: [
      ["Načítame údaje systému DPF", "Skontrolujeme chybové kódy, stav regenerácie a dostupné prevádzkové hodnoty."],
      ["Preveríme tlak a snímače", "Vyhodnotíme diferenčný tlak, teplotné údaje a súvisiace snímače podľa možností vozidla."],
      ["Posúdime vhodný ďalší krok", "Podľa nameraných dát odporučíme servis, regeneráciu, opravu súvisiacej chyby alebo ďalšiu kontrolu."],
    ],
    outcomes: [
      "Čo dostupné údaje hovoria o stave filtra a regenerácie.",
      "Či problém môže súvisieť so snímačom alebo iným emisným systémom.",
      "Ktorý ďalší postup je podľa merania vhodný a bezpečný."],
    faq: [
      ["Musí sa každý zanesený DPF vymeniť?", "Nie. Najprv treba posúdiť stav filtra, údaje o tlaku, regeneráciu a príčinu problému. Výmena je iba jedna z možných možností."],
      ["Pomôže nútená regenerácia vždy?", "Nie. Regenerácia je vhodná iba vtedy, keď to stav filtra, vozidla a prevádzkové podmienky umožňujú."],
      ["Môžem pokračovať v jazde s kontrolkou DPF?", "Postupujte podľa návodu vozidla. Ak kontrolka bliká, pribudne ďalšia výstraha alebo vozidlo stratí výkon, ďalšiu jazdu neriskujte a dohodnite kontrolu."],
    ],
    related: ["diagnostika-oprava-egr", "diagnostika-emisnych-systemov", "diagnostika-oprava-adblue"],
  },
  "diagnostika-oprava-adblue": {
    reasons: [
      "Vozidlo zobrazuje výstrahu systému AdBlue alebo SCR.",
      "Po doplnení AdBlue výstraha nezmizla.",
      "Na displeji sa zobrazuje odpočet do obmedzenia štartu.",
      "Kontrolka motora sprevádza chybu dávkovania alebo snímača NOx.",
    ],
    steps: [
      ["Preveríme hlásenia a dáta", "Načítame chyby systému SCR a dostupné hodnoty dávkovania, tlaku a snímačov."],
      ["Skontrolujeme jednotlivé časti", "Podľa chyby preveríme čerpadlo, vstrekovanie AdBlue, vedenie a súvisiace snímače."],
      ["Určíme rozsah opravy", "Zistenia spojíme do odporúčania, ktoré sa týka konkrétnej chybnej alebo nefunkčnej časti."],
    ],
    outcomes: [
      "Ktorá časť systému SCR podľa údajov nepracuje správne.",
      "Či treba ďalšie meranie alebo možno navrhnúť konkrétnu opravu.",
      "Aký postup je potrebný pred ďalšou prevádzkou vozidla."],
    faq: [
      ["Prečo výstraha nezmizla po doplnení AdBlue?", "Nízka hladina nie je jedinou možnou príčinou. Výstraha môže súvisieť s dávkovaním, tlakom alebo snímačmi a vyžaduje diagnostiku."],
      ["Je chyba NOx snímača vždy chybou samotného snímača?", "Nie vždy. Chybový kód treba porovnať s ďalšími údajmi systému, napájaním a prevádzkovými podmienkami."],
      ["Dá sa zrušiť odpočet do zákazu štartu?", "Najprv treba odstrániť potvrdenú príčinu chyby. Samotné vymazanie hlásenia bez opravy nie je riešením."],
    ],
    related: ["diagnostika-emisnych-systemov", "diagnostika-oprava-egr", "diagnostika-servis-dpf"],
  },
  "diagnostika-emisnych-systemov": {
    reasons: [
      "Svieti kontrolka motora alebo výstraha emisného systému.",
      "Vozidlo neprešlo emisnou kontrolou alebo sa na ňu pripravujete po zistenej chybe.",
      "Vozidlo stráca výkon a diagnostika eviduje viac súvisiacich chýb.",
      "Chyby DPF, EGR alebo SCR sa opakovane vracajú.",
    ],
    steps: [
      ["Získame spoločný obraz", "Načítame chyby a prevádzkové dáta motora aj dostupných emisných systémov."],
      ["Porovnáme súvisiace hodnoty", "Preveríme väzby medzi EGR, DPF, SCR a snímačmi podľa výbavy vozidla."],
      ["Zoradíme ďalšie kroky", "Určíme, ktorú chybu treba riešiť ako prvú a ktoré kontroly majú nasledovať."],
    ],
    outcomes: [
      "Ktorý emisný systém alebo snímač si vyžaduje pozornosť.",
      "Či spolu zaznamenané chyby pravdepodobne súvisia.",
      "V akom poradí odporúčame pokračovať v kontrole alebo oprave."],
    faq: [
      ["Je emisná diagnostika iba vymazanie chýb?", "Nie. Chybové kódy porovnávame so živými dátami a vzťahmi medzi jednotlivými systémami."],
      ["Môže jedna chyba ovplyvniť viac emisných systémov?", "Áno. Systémy používajú spoločné údaje motora a snímačov, preto je dôležité posudzovať ich v súvislostiach."],
      ["Viete zaručiť úspech na emisnej kontrole?", "Nie je možné ho zaručiť iba diagnostikou. Vieme však pomenovať zistené chyby a odporučiť, čo treba pred kontrolou vyriešiť."],
    ],
    related: ["diagnostika-servis-dpf", "diagnostika-oprava-egr", "diagnostika-oprava-adblue"],
  },
  pneuservis: {
    reasons: [
      "Blíži sa sezónna výmena pneumatík alebo kolies.",
      "Pri určitej rýchlosti cítite vibrácie vo volante alebo vozidle.",
      "Pneumatika opakovane stráca tlak.",
      "Vidíte nerovnomerné opotrebovanie, prasklinu, rez alebo vydutie.",
    ],
    urgentNote: "S pneumatikou s vydutím, hlbokým rezom alebo rýchlym únikom tlaku nepokračujte v jazde. Poškodené koleso vymeňte alebo zabezpečte bezpečný odvoz vozidla.",
    steps: [
      ["Prezrieme pneumatiky a kolesá", "Skontrolujeme dezén, viditeľné poškodenia a stav plôch dôležitých pre montáž."],
      ["Prezujeme a vyvážime", "Pneumatiky alebo kolesá namontujeme a vyvážime podľa dohodnutého rozsahu."],
      ["Nastavíme tlak a zhrnieme stav", "Skontrolujeme tlak a upozorníme na poškodenie alebo opotrebovanie, ktoré si vyžaduje riešenie."],
    ],
    outcomes: [
      "Aký je viditeľný stav dezénu a pneumatík.",
      "Či sa kolesá podarilo správne vyvážiť.",
      "Či niektorá pneumatika potrebuje opravu, odborné posúdenie alebo výmenu."],
    faq: [
      ["Prečo volant pri vyššej rýchlosti vibruje?", "Jednou z možností je nevyvážené koleso, ale vibrácie môžu mať aj inú príčinu. Začneme kontrolou kolies a podľa výsledku odporučíme ďalší krok."],
      ["Stačí iba dohustiť pneumatiku, ktorá stráca tlak?", "Nie, ak sa pokles opakuje. Treba skontrolovať pneumatiku, ventil aj dosadnutie na disk a zistiť miesto úniku."],
      ["Kedy treba pneumatiku vymeniť?", "Rozhoduje hĺbka dezénu, vek, viditeľné poškodenie a podmienky používania. Po kontrole vysvetlíme konkrétne zistenie."],
    ],
    related: ["brzdy-a-podvozok", "pravidelny-servis", "konzultacia"],
  },
  konzultacia: {
    reasons: [
      "Viete opísať prejav, ale neviete, ktorú službu vybrať.",
      "Problém sa objavuje nepravidelne a zatiaľ sa ho nepodarilo zachytiť.",
      "Rozsvietilo sa viac kontroliek alebo sa objavilo viac príznakov naraz.",
      "Chcete si pred opravou ujasniť rozsah a vhodný prvý krok.",
    ],
    steps: [
      ["Vypočujeme si prejavy", "Prejdeme, kedy problém vzniká, ako sa vozidlo správa a čo sa na ňom už riešilo."],
      ["Zaradíme problém do oblasti", "Podľa informácií určíme, či má nasledovať diagnostika, mechanická kontrola alebo konkrétna služba."],
      ["Dohodneme ďalší krok", "Upresníme vhodný rozsah, termín a informácie, ktoré budeme pri kontrole potrebovať."],
    ],
    outcomes: [
      "Ktorý typ kontroly alebo diagnostiky je vhodné zvoliť ako prvý.",
      "Aké informácie alebo podmienky pomôžu problém zachytiť.",
      "Čo bude zahŕňať nasledujúca návšteva a na čom sa treba vopred dohodnúť."],
    faq: [
      ["Viete určiť poruchu iba telefonicky?", "Nie spoľahlivo. Telefonát pomôže zvoliť správny typ kontroly, ale príčinu je zvyčajne potrebné overiť na vozidle."],
      ["Čo si mám pripraviť?", "Značku, model, rok výroby, popis prejavu, informáciu o kontrolkách a prehľad nedávnych opráv alebo udalostí."],
      ["Zahŕňa konzultácia automaticky aj opravu?", "Nie. Najprv si dohodneme vhodnú kontrolu a až podľa výsledku samostatne odsúhlasíme prípadnú opravu."],
    ],
    related: ["diagnostika", "pravidelny-servis", "diagnostika-emisnych-systemov"],
  },
} as const satisfies Record<ServiceSlug, ServiceDetailExtra>;

type Page = "home" | "services" | "about" | "contact" | "privacy" | "cookies";
type Route = { page: Page; serviceSlug?: string; notFound?: boolean };

const navItems = ["services", "about", "contact"] as const satisfies readonly Page[];
const pagePaths = { home: "", services: "sluzby", about: "o-nas", contact: "kontakt", privacy: "ochrana-osobnych-udajov", cookies: "cookies" } as const satisfies Record<Page, string>;
const pageByPath = new Map<string, Page>([
  [pagePaths.home, "home"],
  [pagePaths.services, "services"],
  [pagePaths.about, "about"],
  [pagePaths.contact, "contact"],
  [pagePaths.privacy, "privacy"],
  [pagePaths.cookies, "cookies"],
]);
const netlifyHoneypotProps = { "netlify-honeypot": "bot-field" } as const;
const desktopNavLinkClassName = "relative flex h-[76px] items-center justify-center bg-transparent p-0 text-sm font-semibold text-primary-foreground after:absolute after:inset-x-0 after:bottom-5 after:h-0.5 after:bg-accent after:opacity-0 after:transition-opacity hover:bg-transparent hover:text-primary-foreground hover:after:opacity-100 focus:bg-transparent focus:text-primary-foreground focus-visible:after:opacity-100 data-[active=true]:bg-transparent data-[active=true]:text-primary-foreground data-[active=true]:after:opacity-100";
const footerLinkClassName = "max-w-fit text-sm text-white/70 transition-colors hover:text-white";

function getRoute(pathname: string): Route {
  const path = pathname.replace(/\/$/, "") || "/";
  const segments = path.split("/").filter(Boolean);
  const page = pageByPath.get(segments[0] ?? "");

  if (!page) return { page: "home", notFound: segments.length > 0 };
  if (page === "services") {
    const serviceSlug = segments[1];
    const serviceExists = !serviceSlug || content.sk.services.items.some((item) => item.slug === serviceSlug);
    return { page, serviceSlug, notFound: segments.length > 2 || !serviceExists };
  }

  return { page, notFound: segments.length > 1 };
}

function href(page: Page): string {
  const path = pagePaths[page];
  return path ? `/${path}/` : "/";
}

function serviceHref(slug: string): string {
  return `${href("services")}${slug}/`;
}

export function App({ pathname = "/" }: { pathname?: string }) {
  const route = getRoute(pathname);
  const page = route.page;
  const copy = content.sk;
  const seoPage = getSeoPage(pathname);

  useEffect(() => {
    document.documentElement.lang = "sk";
    applySeoMetadata(seoPage, route.notFound);
  }, [route.notFound, seoPage]);

  return (
    <>
      <a className="sr-only fixed left-4 top-4 z-50 bg-accent px-4 py-3 font-semibold text-accent-foreground focus:not-sr-only focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid focus-visible:outline-current" href="#obsah">Preskočiť na obsah</a>
      <SiteHeader copy={copy} page={page} />
      <main id="obsah" tabIndex={-1} className="min-h-dvh bg-background text-foreground">
        <PageContent copy={copy} route={route} />
      </main>
      <SiteFooter copy={copy} />
      <Toaster theme="light" position="bottom-right" />
    </>
  );
}

function PageContent({ copy, route }: { copy: Copy; route: Route }) {
  if (route.notFound) return <NotFoundPage copy={copy} />;

  switch (route.page) {
    case "home":
      return <HomePage copy={copy} />;
    case "services": {
      const service = route.serviceSlug ? copy.services.items.find((item) => item.slug === route.serviceSlug) : undefined;
      return service ? <ServiceDetailPage copy={copy} service={service} /> : <ServicesPage copy={copy} />;
    }
    case "about":
      return <AboutPage copy={copy} />;
    case "contact":
      return <ContactPage copy={copy} />;
    case "privacy":
      return <PrivacyPage />;
    case "cookies":
      return <CookiesPage />;
  }
}

function SiteHeader({ copy, page }: { copy: Copy; page: Page }) {
  const hasScrolled = useHeaderShadow();

  return (
    <header className={cn("sticky top-0 z-50 border-b border-white/20 bg-primary text-primary-foreground transition-shadow", hasScrolled && "shadow-xl shadow-primary/25")}>
      <div className="container flex h-[76px] items-center justify-between gap-5">
        <a href={href("home")} aria-label={copy.logoAlt}>
          <img src={whiteLogo} alt={copy.logoAlt} width="220" height="44" className="h-7 w-auto sm:h-8" />
        </a>
        <nav aria-label="Hlavná navigácia" className="hidden lg:block">
          <NavigationMenu viewport={false}>
            <NavigationMenuList className="gap-7">
              <NavigationMenuItem>
                <NavigationMenuTrigger className={cn(desktopNavLinkClassName, "gap-1 data-[state=open]:bg-transparent data-[state=open]:text-primary-foreground data-[state=open]:after:opacity-100 data-[state=open]:hover:bg-transparent data-[state=open]:focus:bg-transparent", page === "services" && "after:opacity-100")}>
                  {copy.labels.services}
                </NavigationMenuTrigger>
                <NavigationMenuContent className="w-240 max-w-[calc(100vw-2rem)] overflow-hidden border-border/70 bg-popover p-0 shadow-2xl shadow-primary/20 md:left-1/2 md:w-240 md:-translate-x-1/2">
                  <div className="flex items-center justify-between gap-4 border-b bg-muted/40 px-4 py-3">
                    <div>
                      <p className="font-semibold text-foreground">{copy.labels.services}</p>
                    </div>
                    <Button asChild variant="accent" size="default">
                      <a href={href("services")}>{copy.labels.allServices}<ArrowUpRight data-icon="inline-end" aria-hidden="true" /></a>
                    </Button>
                  </div>
                  <ul className="grid grid-flow-col grid-cols-3 grid-rows-3">
                    {copy.services.items.map((serviceItem, index) => (
                      <li key={serviceItem.slug}>
                        <NavigationMenuLink asChild className={cn("group/service h-full flex-row items-center gap-3 border-b border-r p-4 hover:bg-muted hover:text-foreground focus:bg-muted focus:text-foreground", index % 3 === 2 && "border-b-0", index >= 6 && "border-r-0")}>
                          <a href={serviceHref(serviceItem.slug)}>
                            <span className="text-xs font-bold tracking-[.16em] text-accent">{serviceItem.number}</span>
                            <strong className="flex-1 text-sm text-foreground">{serviceItem.title}</strong>
                            <ArrowUpRight className="text-muted-foreground transition-transform group-hover/service:-translate-y-0.5 group-hover/service:translate-x-0.5 group-hover/service:text-accent" size={16} aria-hidden="true" />
                          </a>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </NavigationMenuItem>
              {(["about", "contact"] as const).map((item) => (
                <NavigationMenuItem key={item}>
                  <NavigationMenuLink asChild active={page === item} className={desktopNavLinkClassName}>
                    <a href={href(item)}>{copy.labels[item]}</a>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>
        </nav>
        <div className="flex items-center gap-2">
          <Button asChild variant="accent" size="lg" className="hidden sm:inline-flex">
            <a href={href("contact")}>{copy.labels.book}<ArrowUpRight data-icon="inline-end" aria-hidden="true" /></a>
          </Button>
          <Sheet>
            <SheetTrigger asChild><Button variant="inverse-outline" size="icon" className="lg:hidden" aria-label="Otvoriť navigáciu"><Menu /></Button></SheetTrigger>
            <SheetContent className="border-0 bg-primary text-primary-foreground">
              <SheetHeader><SheetTitle className="text-primary-foreground">Navigácia</SheetTitle></SheetHeader>
              <nav aria-label="Mobilná navigácia" className="flex flex-col gap-1 px-4 text-lg font-semibold">
                <SheetClose asChild><a href={href("services")} className="px-3 py-3 hover:bg-white/10">{copy.labels.services}</a></SheetClose>
                <div className="flex flex-col gap-1 border-l border-white/20 pl-4 text-base font-normal text-white/75">
                  {copy.services.items.map((serviceItem) => <SheetClose key={serviceItem.slug} asChild><a href={serviceHref(serviceItem.slug)} className="grid grid-cols-[2rem_1fr] gap-2 py-1 hover:text-white"><span className="text-sm font-semibold text-white/65">{serviceItem.number}</span><span>{serviceItem.title}</span></a></SheetClose>)}
                </div>
                {(["about", "contact"] as const).map((item) => <SheetClose key={item} asChild><a href={href(item)} className="px-3 py-3 hover:bg-white/10">{copy.labels[item]}</a></SheetClose>)}
              </nav>
              <div className="mt-auto flex flex-col gap-2 p-4">{copy.contact.phoneValues.map((phone) => <Button key={phone.href} asChild variant="accent" size="lg" className="w-full"><a href={phone.href}>{phone.value}<Phone data-icon="inline-end" /></a></Button>)}</div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}

function HomePage({ copy }: { copy: Copy }) {
  return (
    <>
      <SplitHero eyebrow={copy.eyebrow} title={copy.headline} text={copy.text} image={images.hero} imageAlt="Mechanik pracujúci na aute v dielni" imageWidth={2200} imageHeight={1467}>
        <Button asChild variant="accent" size="lg">
          <a href={href("contact")}>{copy.labels.book}<ArrowUpRight data-icon="inline-end" aria-hidden="true" /></a>
        </Button>
        <Button asChild variant="inverse-outline" size="lg">
          <a href={phoneNumbers[0].href}>{copy.labels.call}<ArrowUpRight data-icon="inline-end" aria-hidden="true" /></a>
        </Button>
      </SplitHero>

      <QuickLinks copy={copy} />
      <ServicesLead copy={copy} />
      <ServiceList items={copy.services.items} />
      <AboutPanel copy={copy} compact />
      <ProcessPanel copy={copy} />
      <HoursPanel copy={copy} />
      <HomeContact copy={copy} />
    </>
  );
}

function ServicesPage({ copy }: { copy: Copy }) {
  return (
    <>
      <SplitHero eyebrow={copy.services.kicker} title={copy.services.title} text={copy.services.text} image={images.servicesHero} imageAlt="Mechanik pri kontrole motora vozidla" imageWidth={1920} imageHeight={1280}>
        <Button asChild variant="accent" size="lg">
          <a href={href("contact")}>{copy.labels.book}<ArrowUpRight data-icon="inline-end" aria-hidden="true" /></a>
        </Button>
      </SplitHero>
      <ServiceList items={copy.services.items} tone="light" />
      <HomeContact copy={copy} />
    </>
  );
}

function AboutPage({ copy }: { copy: Copy }) {
  return (
    <>
      <AboutHero copy={copy} />
      <WhyUsPanel copy={copy} />
      <ProcessPanel copy={copy} compact />
      <FaqPanel copy={copy} />
      <LocationMap copy={copy} compact />
    </>
  );
}

function ContactPage({ copy }: { copy: Copy }) {
  return (
    <>
      <HomeContact copy={copy} pageHeading />
      <CompanyDetails copy={copy} />
      <LocationMap copy={copy} />
    </>
  );
}

function NotFoundPage({ copy }: { copy: Copy }) {
  return (
    <section className="grid min-h-[calc(100svh-76px)] bg-primary text-primary-foreground lg:grid-cols-[.9fr_1.1fr]" aria-labelledby="not-found-title">
      <div className="flex items-center px-5 py-20 sm:px-8 lg:px-12 xl:px-[max(3rem,calc((100vw-80rem)/2))]">
        <div className="max-w-xl">
          <p className="text-sm font-semibold tracking-[.16em] text-white/65 uppercase">Chyba 404</p>
        <h1 id="not-found-title" className="mt-5 text-5xl font-extrabold tracking-[.01em] sm:text-7xl">Táto stránka sa nenašla.</h1>
          <p className="mt-6 max-w-lg text-lg leading-8 text-white/75">Adresa, ktorú hľadáte, už neplatí alebo ste odbočili mimo servisnej trasy.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild variant="accent" size="lg"><a href={href("home")}>Späť na úvod<ArrowUpRight data-icon="inline-end" aria-hidden="true" /></a></Button>
            <Button asChild variant="inverse-outline" size="lg"><a href={href("contact")}>{copy.labels.contact}</a></Button>
          </div>
        </div>
      </div>
      <div className="relative min-h-80 overflow-hidden lg:min-h-0">
        <ResponsiveImage className="absolute inset-0 size-full object-cover" src={images.hero} alt="Autoservis IM Autotech" width={2200} height={1467} fetchPriority="high" sizes="(min-width: 1024px) 55vw, 100vw" />
        <div className="absolute inset-0 bg-primary/65" aria-hidden="true" />
        <span className="absolute inset-x-5 bottom-4 text-[clamp(8rem,24vw,22rem)] font-extrabold leading-none tracking-[.01em] text-white/20 lg:inset-x-10 lg:bottom-8" aria-hidden="true">404</span>
      </div>
    </section>
  );
}

function QuickLinks({ copy }: { copy: Copy }) {
  return (
    <section className="border-b" aria-label={copy.labels.contact}>
      <div className="container grid md:grid-cols-4">
        {copy.quick.map(([label, value, link, Icon]) => (
          <a key={link} href={link} target={link.startsWith("http") ? "_blank" : undefined} rel={link.startsWith("http") ? "noreferrer" : undefined} className="group flex min-w-0 items-center gap-3 border-b p-5 last:border-b-0 hover:bg-muted md:border-r md:last:border-r-0 md:border-b-0">
            <Icon className="text-accent" aria-hidden="true" />
            <span className="flex min-w-0 flex-1 flex-col gap-1"><small className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">{label}</small><strong className="whitespace-pre-line text-sm font-semibold leading-5 sm:text-base">{value}</strong></span>
            <ArrowUpRight className="text-muted-foreground transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" aria-hidden="true" />
          </a>
        ))}
      </div>
    </section>
  );
}

function ServicesLead({ copy }: { copy: Copy }) {
  return (
    <section className="py-20 sm:py-28">
      <div className="container grid items-center gap-12 lg:grid-cols-2">
        <div>
          <p className="text-sm font-semibold tracking-[.16em] text-accent uppercase">{copy.services.kicker}</p>
          <h2 className="mt-4 text-4xl font-extrabold tracking-[.01em] sm:text-6xl">{copy.services.title}</h2>
          <p className="mt-6 max-w-xl text-lg leading-8 text-muted-foreground">{copy.services.text}</p>
          <TextLink href={href("services")}>{copy.labels.allServices}</TextLink>
        </div>
        <figure className="min-h-80 overflow-hidden">
          <ResponsiveImage className="size-full object-cover outline outline-1 outline-[rgb(0_0_0_/_10%)]" src={images.maintenance} alt="Ruky pri servise vozidla v dielni" width={1000} height={750} loading="lazy" sizes="(min-width: 1024px) 50vw, 100vw" />
        </figure>
      </div>
    </section>
  );
}

function ServiceList({ items, tone = "dark" }: { items: readonly ServiceItem[]; tone?: "dark" | "light" }) {
  const isDark = tone === "dark";

  return (
    <section className={cn("overflow-hidden py-20 sm:py-28", isDark ? "bg-primary text-primary-foreground" : "bg-background text-foreground")} aria-labelledby={`services-grid-title-${tone}`}>
      <div className="container">
        <div className={cn("mb-12 grid gap-6 border-b pb-6 lg:grid-cols-[1fr_auto] lg:items-end", isDark ? "border-white/25" : "border-border")}>
          <div>
            <p className={cn("text-sm font-semibold tracking-[.16em] uppercase", isDark ? "text-white/65" : "text-accent")}>Ponuka servisu</p>
            <h2 id={`services-grid-title-${tone}`} className="mt-3 text-3xl font-extrabold tracking-[.01em] sm:text-5xl">Vyberte službu pre vaše auto.</h2>
          </div>
          <p className={cn("max-w-sm text-sm leading-6 lg:text-right", isDark ? "text-white/65" : "text-muted-foreground")}>Každú opravu začíname kontrolou stavu a jasnou dohodou o ďalšom postupe.</p>
        </div>
        <div className={cn("grid border-l border-t md:grid-cols-2 lg:grid-cols-3", isDark ? "border-white/20" : "border-border")}>
          {items.map((service) => <ServiceCard key={service.number} service={service} tone={tone} />)}
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ service, tone }: { service: ServiceItem; tone: "dark" | "light" }) {
  const isDark = tone === "dark";

  return (
    <a href={serviceHref(service.slug)} className={cn("group flex min-h-full flex-col border-r border-b outline-none transition-colors duration-300 focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-inset", isDark ? "border-white/20 bg-primary hover:bg-white/6" : "border-border bg-background hover:bg-muted")}>
      <div className="aspect-16/10 overflow-hidden bg-muted">
        <ResponsiveImage className={cn("size-full object-cover outline outline-1 transition-all duration-500 group-hover:scale-105 group-hover:opacity-100 group-focus-visible:scale-105 group-focus-visible:opacity-100", isDark ? "opacity-75 outline-[rgb(255_255_255_/_10%)]" : "opacity-90 outline-[rgb(0_0_0_/_10%)]")} src={service.image} alt="" width={1200} height={800} loading="lazy" sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw" />
      </div>
      <div className="flex grow flex-col p-6 sm:p-7">
        <div className="flex items-center justify-between gap-5">
          <span className={cn("text-sm font-semibold tracking-[.16em]", isDark ? "text-white/55" : "text-muted-foreground")}>{service.number}</span>
          <span className={cn("flex size-10 items-center justify-center border transition-colors group-hover:border-accent group-hover:bg-accent group-hover:text-accent-foreground group-focus-visible:border-accent group-focus-visible:bg-accent group-focus-visible:text-accent-foreground", isDark ? "border-white/35" : "border-border")}><ArrowUpRight aria-hidden="true" /></span>
        </div>
        <h3 className="mt-8 text-2xl font-extrabold tracking-[.01em]">{service.title}</h3>
        <p className={cn("mt-3 leading-7", isDark ? "text-white/65" : "text-muted-foreground")}>{service.text}</p>
      </div>
    </a>
  );
}

function ServiceDetailPage({ copy, service }: { copy: Copy; service: ServiceItem }) {
  const extra = serviceDetailExtras[service.slug];

  return (
    <>
      <SplitHero eyebrow={service.number} eyebrowVariant="number" title={service.title} text={service.text} image={service.image} imageAlt={service.title} imageWidth={1600} imageHeight={1100} compactTitle>
        <Button asChild variant="accent" size="lg">
          <a href="#kontakt">{copy.serviceDetail.cta}<ArrowUpRight data-icon="inline-end" aria-hidden="true" /></a>
        </Button>
        <Button asChild variant="inverse-outline" size="lg">
          <a href={href("services")}><ArrowLeft data-icon="inline-start" aria-hidden="true" />{copy.serviceDetail.back}</a>
        </Button>
      </SplitHero>
      <ServiceReasons service={service} extra={extra} />
      <ServiceJourney extra={extra} />
      <ServiceOutcomes copy={copy} service={service} extra={extra} />
      <ServiceFaq service={service} extra={extra} />
      <RelatedServices copy={copy} extra={extra} />
      <HomeContact copy={copy} />
    </>
  );
}

function ServiceReasons({ service, extra }: { service: ServiceItem; extra: ServiceDetailExtra }) {
  const headingId = `reasons-${service.slug}`;

  return (
    <section className="py-20 sm:py-28" aria-labelledby={headingId}>
      <div className="container grid gap-12 lg:grid-cols-[.8fr_1.2fr]">
        <div>
          <p className="text-sm font-semibold tracking-[.16em] text-accent uppercase">Kedy sa objednať</p>
          <h2 id={headingId} className="mt-4 text-3xl font-extrabold tracking-[.01em] sm:text-5xl">Toto sú dobré dôvody na kontrolu.</h2>
          <p className="mt-6 max-w-xl leading-7 text-muted-foreground">Rovnaký prejav môže mať viac príčin. Berte ho preto ako dôvod na kontrolu, nie ako hotovú diagnózu.</p>
        </div>
        <div>
          <ol className="grid border-l border-t sm:grid-cols-2">
            {extra.reasons.map((reason, index) => (
              <li key={reason} className="flex min-h-40 flex-col border-r border-b p-6">
                <span className="font-mono text-sm font-bold tracking-[.12em] text-accent">0{index + 1}</span>
                <p className="mt-auto pt-8 font-semibold leading-7">{reason}</p>
              </li>
            ))}
          </ol>
          {extra.urgentNote ? <p className="border-x border-b border-accent/30 bg-accent/8 p-5 text-sm font-semibold leading-6 text-foreground">{extra.urgentNote}</p> : null}
        </div>
      </div>
    </section>
  );
}

function ServiceJourney({ extra }: { extra: ServiceDetailExtra }) {
  return (
    <section className="bg-primary py-20 text-primary-foreground sm:py-28" aria-labelledby="service-journey-title">
      <div className="container">
        <p className="text-sm font-semibold tracking-[.16em] text-white/65 uppercase">Ako postupujeme</p>
        <h2 id="service-journey-title" className="mt-4 text-3xl font-extrabold tracking-[.01em] sm:text-5xl">Od prejavu k jasnému ďalšiemu kroku.</h2>
        <div className="mt-14 grid gap-8 md:grid-cols-3">
          {extra.steps.map(([title, text], index) => (
            <article key={title} className="border-t border-white/30 pt-6">
              <Badge variant="outline" className="border-white/30 text-white">0{index + 1}</Badge>
              <h3 className="mt-6 text-xl font-bold">{title}</h3>
              <p className="mt-3 leading-7 text-white/70">{text}</p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServiceOutcomes({ copy, service, extra }: { copy: Copy; service: ServiceItem; extra: ServiceDetailExtra }) {
  return (
    <section className="py-20 sm:py-28" aria-labelledby="service-outcomes-title">
      <div className="container grid gap-12 lg:grid-cols-[.85fr_1.15fr] lg:items-start">
        <div>
          <p className="text-sm font-semibold tracking-[.16em] text-accent uppercase">{copy.serviceDetail.included}</p>
          <h2 id="service-outcomes-title" className="mt-4 text-3xl font-extrabold tracking-[.01em] sm:text-5xl">{service.detailTitle}</h2>
          <p className="mt-6 max-w-xl text-lg leading-8 text-muted-foreground">{service.detailText}</p>
        </div>
        <div>
          <p className="text-sm font-semibold tracking-[.16em] text-muted-foreground uppercase">Po kontrole budete vedieť</p>
          <ul className="mt-4 border-t">
            {extra.outcomes.map((outcome) => (
              <li key={outcome} className="flex gap-4 border-b py-6">
                <span className="flex size-9 shrink-0 items-center justify-center bg-accent text-accent-foreground" aria-hidden="true"><Check size={18} /></span>
                <p className="pt-1 font-semibold leading-7">{outcome}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function ServiceFaq({ service, extra }: { service: ServiceItem; extra: ServiceDetailExtra }) {
  const headingId = `service-faq-${service.slug}`;

  return (
    <section className="bg-muted/60 py-20 sm:py-28" aria-labelledby={headingId}>
      <div className="container grid gap-12 lg:grid-cols-[.75fr_1.25fr]">
        <div>
          <p className="text-sm font-semibold tracking-[.16em] text-accent uppercase">Časté otázky</p>
          <h2 id={headingId} className="mt-4 max-w-xl text-3xl font-extrabold tracking-[.01em] sm:text-5xl">Čo je dobré vedieť.</h2>
        </div>
        <Accordion items={extra.faq} idPrefix={`service-${service.slug}`} />
      </div>
    </section>
  );
}

function RelatedServices({ copy, extra }: { copy: Copy; extra: ServiceDetailExtra }) {
  const relatedServices = extra.related.map((slug) => copy.services.items.find((item) => item.slug === slug)).filter((item): item is ServiceItem => Boolean(item));

  return (
    <section className="py-20 sm:py-28" aria-labelledby="related-services-title">
      <div className="container">
        <div className="flex flex-col gap-5 border-b pb-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-semibold tracking-[.16em] text-accent uppercase">Súvisiace služby</p>
            <h2 id="related-services-title" className="mt-3 text-3xl font-extrabold tracking-[.01em] sm:text-5xl">Možno sa vám bude hodiť.</h2>
          </div>
          <Button asChild variant="accent" size="lg"><a href={href("services")}>{copy.labels.allServices}<ArrowUpRight data-icon="inline-end" aria-hidden="true" /></a></Button>
        </div>
        <div className="mt-10 grid border-l border-t md:grid-cols-3">
          {relatedServices.map((related) => <ServiceCard key={related.slug} service={related} tone="light" />)}
        </div>
      </div>
    </section>
  );
}

function AboutPanel({ copy, compact = false }: { copy: Copy; compact?: boolean }) {
  return (
    <section className="py-20 sm:py-28">
      <div className="container grid gap-12 lg:grid-cols-2">
        <div>
          <p className="text-sm font-semibold tracking-[.16em] text-accent uppercase">{copy.about.kicker}</p>
          <h2 className="mt-4 text-4xl font-extrabold tracking-[.01em] sm:text-6xl">{copy.about.title}</h2>
          <p className="mt-6 max-w-xl text-lg leading-8 text-muted-foreground">{copy.about.text}</p>
          {compact ? <TextLink href={href("about")}>{copy.labels.learnMore}</TextLink> : null}
        </div>
        <div className="flex flex-col">
          {copy.about.points.map((point) => <div key={point} className="flex items-center gap-4 first:border-t border-b py-6"><span className="flex size-10 items-center justify-center bg-accent text-accent-foreground"><Check /></span><p className="font-semibold">{point}</p></div>)}
        </div>
      </div>
    </section>
  );
}

function AboutHero({ copy }: { copy: Copy }) {
  const pageCopy = copy.about.page;

  return (
    <SplitHero eyebrow={pageCopy.kicker} title={pageCopy.title} text={pageCopy.text} image={images.about} imageAlt={pageCopy.imageAlt} imageWidth={1920} imageHeight={1080}>
      <Button asChild variant="accent" size="lg">
        <a href={href("contact")}>{pageCopy.cta}<ArrowUpRight data-icon="inline-end" aria-hidden="true" /></a>
      </Button>
    </SplitHero>
  );
}

function SplitHero({ eyebrow, eyebrowVariant = "label", title, text, image, imageAlt, imageWidth, imageHeight, compactTitle = false, children }: { eyebrow: string; eyebrowVariant?: "label" | "number"; title: string; text: string; image: string; imageAlt: string; imageWidth: number; imageHeight: number; compactTitle?: boolean; children: ReactNode }) {
  return (
    <section className="site-hero relative overflow-hidden bg-primary text-primary-foreground">
      <div className="container">
        <div className="flex items-center py-16 sm:py-20 lg:min-h-[calc(88svh-76px)] lg:w-[44%] lg:py-24">
          <div className="site-hero-copy max-w-xl">
            {eyebrowVariant === "number" ? <span className="inline-flex size-11 items-center justify-center border border-white/35 font-mono text-sm font-bold tracking-[.12em] text-white">{eyebrow}</span> : <p className="text-sm font-semibold tracking-[.16em] text-white/65 uppercase">{eyebrow}</p>}
            <h1 className={cn("mt-5 font-extrabold leading-[1.02] tracking-[.01em]", compactTitle ? "text-4xl sm:text-5xl" : "text-4xl sm:text-5xl lg:text-6xl")}>{title}</h1>
            <p className="mt-7 text-lg leading-8 text-white/75">{text}</p>
            <div className="mt-8 flex flex-wrap gap-3">{children}</div>
          </div>
        </div>
      </div>
      <figure className="site-hero-image relative min-h-96 overflow-hidden lg:absolute lg:inset-y-0 lg:left-[48%] lg:min-h-0 lg:w-[52%]">
        <ResponsiveImage className="absolute inset-0 size-full object-cover outline outline-1 outline-[rgb(255_255_255_/_10%)]" src={image} alt={imageAlt} width={imageWidth} height={imageHeight} fetchPriority="high" sizes="(min-width: 1024px) 52vw, 100vw" />
        <div className="absolute inset-0 bg-linear-to-r from-primary/30 to-transparent lg:from-primary/60" aria-hidden="true" />
      </figure>
    </section>
  );
}

function WhyUsPanel({ copy }: { copy: Copy }) {
  const pageCopy = copy.about.page;

  return (
    <section className="about-section-reveal py-20 sm:py-28" aria-labelledby="why-us-title">
      <div className="container">
        <div className="grid gap-8 lg:grid-cols-[.85fr_1.15fr] lg:items-end">
          <div>
            <p className="text-sm font-semibold tracking-[.16em] text-accent uppercase">{pageCopy.whyKicker}</p>
            <h2 id="why-us-title" className="mt-4 max-w-2xl text-3xl font-extrabold tracking-[.01em] sm:text-5xl">{pageCopy.whyTitle}</h2>
          </div>
          <p className="max-w-2xl text-lg leading-8 text-muted-foreground lg:justify-self-end">{pageCopy.whyText}</p>
        </div>
        <div className="mt-14 grid border-l border-t sm:grid-cols-2 lg:grid-cols-4">
          {pageCopy.benefits.map(([title, text], index) => {
            const Icon = aboutBenefitIcons[index] ?? Check;
            return (
              <article key={title} className="about-benefit group flex min-h-72 flex-col border-r border-b p-6 transition-colors duration-300 hover:bg-primary hover:text-primary-foreground sm:p-7">
                <span className="flex size-12 items-center justify-center bg-accent text-accent-foreground transition-transform duration-300 group-hover:-translate-y-1" aria-hidden="true"><Icon /></span>
                <h3 className="mt-auto pt-12 text-xl font-bold">{title}</h3>
                <p className="mt-3 leading-7 text-muted-foreground transition-colors group-hover:text-white/70">{text}</p>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ProcessPanel({ copy, compact = false }: { copy: Copy; compact?: boolean }) {
  return (
    <section className="bg-primary py-20 text-primary-foreground sm:py-28">
      <div className="container">
        <p className="text-sm font-semibold tracking-[.16em] text-white/65 uppercase">{copy.process.kicker}</p>
        <h2 className={cn("mt-4 font-extrabold tracking-[.01em]", compact ? "text-3xl sm:text-5xl" : "text-4xl sm:text-6xl")}>{copy.process.title}</h2>
        <div className="mt-14 grid gap-8 md:grid-cols-3">
          {copy.process.items.map(([number, title, text]) => (
            <article key={number} className="border-t border-white/35 pt-6"><Badge variant="outline" className="border-white/30 text-white">{number}</Badge><h3 className={cn("mt-6 font-bold", compact ? "text-xl" : "text-2xl")}>{title}</h3><p className="mt-3 leading-7 text-white/70">{text}</p></article>
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqPanel({ copy }: { copy: Copy }) {
  const pageCopy = copy.about.page;

  return (
    <section className="about-section-reveal py-20 sm:py-28" aria-labelledby="faq-title">
      <div className="container grid gap-12 lg:grid-cols-[.75fr_1.25fr]">
        <div>
          <p className="text-sm font-semibold tracking-[.16em] text-accent uppercase">{pageCopy.faqKicker}</p>
          <h2 id="faq-title" className="mt-4 max-w-xl text-3xl font-extrabold tracking-[.01em] sm:text-5xl">{pageCopy.faqTitle}</h2>
        </div>
        <Accordion items={pageCopy.faq} idPrefix="about" />
      </div>
    </section>
  );
}

function Accordion({ items, idPrefix }: { items: readonly (readonly [string, string])[]; idPrefix: string }) {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <div className="border-t">
      {items.map(([question, answer], index) => {
        const isOpen = openIndex === index;
        const questionId = `${idPrefix}-question-${index}`;
        const answerId = `${idPrefix}-answer-${index}`;

        return (
            <div key={question} className="border-b">
              <button id={questionId} type="button" aria-expanded={isOpen} aria-controls={answerId} onClick={() => setOpenIndex((currentIndex) => currentIndex === index ? -1 : index)} className="group/faq flex w-full cursor-pointer items-center justify-between gap-6 py-6 px-2 text-left text-lg font-bold transition-colors duration-300 hover:bg-muted/60 focus-visible:bg-muted/60 sm:text-xl">
                <span className="transition-colors duration-300 group-hover/faq:text-accent group-focus-visible/faq:text-accent">{question}</span>
                <span className={cn("flex size-10 shrink-0 items-center justify-center border transition-colors duration-300 group-hover/faq:border-accent group-hover/faq:bg-accent group-hover/faq:text-accent-foreground group-focus-visible/faq:border-accent group-focus-visible/faq:bg-accent group-focus-visible/faq:text-accent-foreground", isOpen && "border-accent bg-accent text-accent-foreground")} aria-hidden="true">
                  <ChevronDown className={cn("transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)] motion-reduce:transition-none", isOpen && "rotate-180")} />
                </span>
              </button>
              <div id={answerId} role="region" aria-labelledby={questionId} aria-hidden={!isOpen} className={cn("grid transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(.22,1,.36,1)] motion-reduce:transition-none", isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0")}>
                <div className="overflow-hidden">
                  <p className="max-w-2xl pb-7 pl-2 pr-14 leading-7 text-wrap text-muted-foreground">{answer}</p>
                </div>
              </div>
            </div>
        );
      })}
    </div>
  );
}

function HoursPanel({ copy }: { copy: Copy }) {
  return (
    <section id="hodiny" className="scroll-mt-24 py-20 sm:py-28">
      <div className="container grid gap-12 lg:grid-cols-[1fr_.85fr] lg:items-end">
        <div><p className="text-sm font-semibold tracking-[.16em] text-accent uppercase">{copy.labels.hours}</p><h2 className="mt-4 text-4xl font-extrabold tracking-[.01em] sm:text-6xl">{copy.hours.title}</h2><p className="mt-6 max-w-xl text-lg leading-8 text-muted-foreground">{copy.hours.text}</p></div>
        <div className="border-y">{copy.hours.rows.map(([day, time]) => <div key={day} className="flex items-center justify-between gap-5 border-b py-5 last:border-b-0"><span className="font-semibold">{day}</span><strong className="font-mono text-sm">{time}</strong></div>)}</div>
      </div>
    </section>
  );
}

function ContactForm({ copy }: { copy: Copy }) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (isSubmitting) return;

    const form = event.currentTarget;
    const formData = new FormData(form);
    const body = new URLSearchParams(Array.from(formData.entries()).map(([key, value]) => [key, String(value)])).toString();

    setIsSubmitting(true);
    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body,
      });

      if (!response.ok) throw new Error(`Netlify form submission failed with ${response.status}`);

      trackGenerateLead();
      form.reset();
      toast.success(copy.contact.success);
    } catch {
      toast.error(copy.contact.error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form name="contact" method="POST" data-netlify="true" {...netlifyHoneypotProps} onSubmit={handleSubmit} aria-busy={isSubmitting}>
      <input type="hidden" name="form-name" value="contact" />
      <p className="hidden"><label>Nechajte prázdne: <input name="bot-field" /></label></p>
      <FieldGroup className="gap-5">
        <Field>
          <FieldLabel className="text-primary-foreground" htmlFor="name">{copy.contact.name}</FieldLabel>
          <Input className="h-12 border-white/50 text-primary-foreground" id="name" name="Meno" autoComplete="name" placeholder="Vaše meno a priezvisko" required />
        </Field>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field>
            <FieldLabel className="text-primary-foreground" htmlFor="email">{copy.contact.emailLabel}</FieldLabel>
            <Input className="h-12 border-white/50 text-primary-foreground" id="email" name="email" type="email" autoComplete="email" placeholder="priklad@email.sk" required />
          </Field>
          <Field>
            <FieldLabel className="text-primary-foreground" htmlFor="phone">{copy.contact.phone}</FieldLabel>
            <Input className="h-12 border-white/50 text-primary-foreground" id="phone" name="Telefón" type="tel" inputMode="tel" autoComplete="tel" placeholder="+421 111 222 333" required />
          </Field>
        </div>
        <Field>
          <FieldLabel className="text-primary-foreground" htmlFor="vehicle">{copy.contact.vehicle}</FieldLabel>
          <Input className="h-12 border-white/50 text-primary-foreground" id="vehicle" name="Vozidlo" autoComplete="off" placeholder="Napr. Škoda Octavia 2.0 TDI" required />
        </Field>
        <Field>
          <FieldLabel className="text-primary-foreground" htmlFor="message">{copy.contact.message}</FieldLabel>
          <Textarea className="min-h-28 border-white/50 text-primary-foreground" id="message" name="Správa" rows={4} placeholder="Stručne opíšte problém s vozidlom" required />
        </Field>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="max-w-xl text-xs leading-5 text-white/65">
            <span className="block">{copy.contact.privacyNotice}</span>
            <span>{copy.contact.privacyPrefix}{" "}<a className="font-medium text-white underline decoration-white/40 underline-offset-4 transition-colors hover:decoration-white" href={href("privacy")}>{copy.contact.privacyLink}</a>.</span>
          </p>
          <Button type="submit" variant="accent" size="lg" className="w-full shrink-0 sm:w-auto" disabled={isSubmitting} aria-busy={isSubmitting} aria-label={isSubmitting ? `${copy.contact.submit} — ${copy.contact.submitting}` : undefined}>
            {isSubmitting ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : null}
            <span>{copy.contact.submit}</span>
            {!isSubmitting ? <ArrowUpRight data-icon="inline-end" aria-hidden="true" /> : null}
          </Button>
        </div>
      </FieldGroup>
    </form>
  );
}

function HomeContact({ copy, pageHeading = false }: { copy: Copy; pageHeading?: boolean }) {
  const Heading = pageHeading ? "h1" : "h2";

  return (
    <section id="kontakt" className="scroll-mt-20 bg-primary py-20 text-primary-foreground sm:py-28">
      <div className="container grid gap-12 lg:grid-cols-[.9fr_1.1fr]">
        <div>
          <p className="text-sm font-semibold tracking-[.16em] text-white/65 uppercase">{copy.labels.location}</p>
          <Heading className="mt-4 text-4xl font-extrabold tracking-[.01em] sm:text-6xl">{copy.contact.title}</Heading>
          <p className="mt-6 max-w-xl text-lg leading-8 text-white/75">{copy.contact.text}</p>
          <div className="mt-10 flex flex-col gap-4 text-lg font-semibold">
            {copy.contact.phoneValues.map((phone) => <a key={phone.href} className="flex max-w-fit items-center gap-3 hover:text-white/75" href={phone.href}><Phone className="text-accent" />{phone.value}</a>)}
            <a className="flex items-center gap-3 hover:text-white/75 max-w-fit" href={`mailto:${copy.contact.email}`}><Mail className="text-accent" />{copy.contact.email}</a>
          </div>
        </div>
        <ContactForm copy={copy} />
      </div>
    </section>
  );
}

function CompanyDetails({ copy }: { copy: Copy }) {
  return (
    <section className="border-b bg-primary py-16 text-primary-foreground sm:py-20">
      <div className="container grid gap-10 lg:grid-cols-[.9fr_1.1fr] lg:items-start">
        <div>
          <p className="text-sm font-semibold tracking-[.16em] text-white/65 uppercase">{copy.company.title}</p>
          <h2 className="mt-4 text-4xl font-extrabold tracking-[.01em] sm:text-5xl">{copy.company.name}</h2>
        </div>
        <dl className="border-t border-white/25">
          <div className="grid gap-2 border-b border-white/25 py-5 sm:grid-cols-[8rem_1fr] sm:gap-5">
            <dt className="text-sm font-semibold tracking-[.16em] text-white/65 uppercase">IČO</dt>
            <dd className="font-semibold">{copy.company.ico}</dd>
          </div>
          <div className="grid gap-2 border-b border-white/25 py-5 sm:grid-cols-[8rem_1fr] sm:gap-5">
            <dt className="text-sm font-semibold tracking-[.16em] text-white/65 uppercase">DIČ</dt>
            <dd className="font-semibold">{copy.company.dic}</dd>
          </div>
          <div className="grid gap-2 border-b border-white/25 py-5 sm:grid-cols-[8rem_1fr] sm:gap-5">
            <dt className="text-sm font-semibold tracking-[.16em] text-white/65 uppercase">Sídlo</dt>
            <dd className="font-semibold">{copy.company.address}</dd>
          </div>
        </dl>
      </div>
    </section>
  );
}

function LocationMap({ copy, compact = false }: { copy: Copy; compact?: boolean }) {
  const mapHref = "https://maps.app.goo.gl/k6JvXCn5b1G5m52y7";

  return (
    <section className="bg-primary py-5 sm:py-8" id="mapa" aria-labelledby="location-title">
      <div className="container grid overflow-hidden sm:grid-cols-[.72fr_1.28fr]">
        <div className="flex min-h-80 flex-col items-start justify-center bg-accent p-8 text-accent-foreground sm:p-12">
          <MapPin aria-hidden="true" />
          <p className="mt-10 text-sm font-semibold tracking-[.16em] uppercase">{copy.contact.addressLabel}</p>
          <h2 id="location-title" className={cn("mt-3 font-extrabold tracking-[.01em]", compact ? "text-3xl sm:text-4xl" : "text-4xl sm:text-5xl")}>{copy.contact.address}</h2>
          <Button asChild variant="inverse-outline" size="lg" className="mt-8"><a href={mapHref} target="_blank" rel="noreferrer">{copy.contact.directions}<ArrowUpRight data-icon="inline-end" /></a></Button>
        </div>
        {/* oxlint-disable-next-line react-doctor/iframe-missing-sandbox -- Trusted cross-origin Google Maps requires both capabilities; browser same-origin policy still isolates it from this site. */}
        <iframe className="h-full min-h-80 w-full border-0 grayscale transition-[filter] duration-500 hover:grayscale-0" title="Mapa lokality IM Autotech" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2650.9261032697023!2d17.572405500000002!3d48.3619436!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x476ca1881c6d05e1%3A0xd7a6e73fe365ba11!2sIM%20Autotech!5e0!3m2!1sen!2ssk!4v1785255309486!5m2!1sen!2ssk" allowFullScreen loading="lazy" referrerPolicy="strict-origin-when-cross-origin" sandbox="allow-scripts allow-same-origin" />
      </div>
    </section>
  );
}

function PrivacyPage() {
  return (
    <section className="py-20 sm:py-28">
      <div className="container max-w-4xl">
        <h1 className="text-4xl font-extrabold tracking-[.01em] sm:text-6xl">Zásady ochrany osobných údajov</h1>

        <div className="mt-12 space-y-12 text-base leading-relaxed text-foreground/80 sm:mt-16">
          <PrivacySection title="1. Označenie prevádzkovateľa a všeobecné informácie">
            <p>
              Tieto zásady ochrany osobných údajov (ďalej ako „Zásady“) obsahujú informácie o spracúvaní Vašich osobných údajov obchodnou spoločnosťou <strong>IM autotech s. r. o.</strong>, so sídlom Slnečná 588/32B, Ružindol 919 61, IČO: 57 544 247, zapísaná OR OS Trnava, oddiel: Sro, vložka č. 62889/T (ďalej ako „Prevádzkovateľ“ alebo ako „my“ v zodpovedajúcom gramatickom tvare), ku ktorému dochádza:
            </p>
            <ul className="list-disc space-y-3 pl-6 marker:text-accent">
              <li>pri prevádzke webovej stránky a pri ďalších činnostiach Prevádzkovateľa <a className="underline decoration-accent underline-offset-4" href="https://imautotech.sk/">https://imautotech.sk/</a> (ďalej ako „webová stránka“);</li>
              <li>prostredníctvom nadväzujúcich profilov Prevádzkovateľa na sociálnych sieťach Facebook a Instagram;</li>
              <li>pri poskytovaní služieb Prevádzkovateľa inými prostriedkami (pri výkone podnikateľskej činnosti Prevádzkovateľa ako takej);</li>
            </ul>
            <p><strong>pričom vo vyššie uvedených prípadoch dochádza k spracúvaniu osobných údajov zákazníkov Prevádzkovateľa, obchodných partnerov a ich kontaktných osôb (zástupcov) v prípade, ak sú obchodní partneri právnickými osobami, a návštevníkov webovej stránky.</strong></p>
            <p><strong>Prostredníctvom týchto Zásad Vám Prevádzkovateľ poskytuje informácie o tom, prečo sú Vaše osobné údaje spracúvané, akým spôsobom sú spracúvané, ako dlho ich Prevádzkovateľ uchováva, aké sú Vaše práva v súvislosti so spracúvaním Vašich osobných údajov a ďalšie relevantné informácie o spracúvaní Vašich osobných údajov vo vyššie uvedených prípadoch.</strong> Prostredníctvom týchto Zásad si Prevádzkovateľ plní informačnú povinnosť voči všetkým dotknutým osobám tak v prípade, ak Prevádzkovateľ získal osobné údaje priamo od Vás ako dotknutej osoby ako aj v prípade, ak získal Vaše osobné údaje z iného zdroja.</p>
            <p>Prevádzkovateľ spracúva Vaše osobné údaje v súlade s Nariadením Európskeho parlamentu a Rady Európskej Únie 2016/679 o ochrane fyzických osôb pri spracúvaní osobných údajov a o voľnom pohybe takýchto údajov, ktorým sa zrušuje smernica 95/46/ES (všeobecné nariadenie o ochrane údajov) (ďalej ako „Nariadenie“) a ostatnými predpismi o ochrane osobných údajov (Nariadenie a ostatné predpisy o ochrane osobných údajov ďalej spolu aj ako „Predpisy o ochrane osobných údajov“).</p>
            <p>Prevádzkovateľa môžete vo veciach týkajúcich sa spracúvania a ochrany osobných údajov kontaktovať na adrese <strong>IM autotech s. r. o., Slnečná 588/32B, Ružindol 919 61</strong> alebo e-mailom na e-mailovú adresu <a className="underline decoration-accent underline-offset-4" href="mailto:info@imautotech.sk">info@imautotech.sk</a>. Prevádzkovateľa môžete kontaktovať na vyššie uvedených kontaktných adresách v prípade akýchkoľvek otázok týkajúcich sa spracúvania Vašich osobných údajov.</p>
          </PrivacySection>

          <PrivacySection title="2. Kategórie spracúvaných osobných údajov, účely, právne základy spracúvania a doba uchovávania Vašich osobných údajov">
            <p>Prevádzkovateľ spracúva Vaše osobné údaje <strong>v súlade so zásadou minimalizácie</strong> tak, aby mohol plniť uzatvorené zmluvy, zákonné povinnosti, spracúval osobné údaje, na spracovávanie ktorých má oprávnený záujem alebo aby spracúval Vaše osobné údaje, na spracúvanie ktorých ste mu udelili súhlas. <strong>Prevádzkovateľ od Vás vyžaduje vždy iba tie osobné údaje, ktorých spracúvanie je na konkrétny účel spracúvania potrebné.</strong></p>
            <p>Prevádzkovateľ spracúva Vaše osobné údaje iba na odôvodnené účely, po obmedzenú dobu a s využitím maximálnej možnej miery zabezpečenia. <strong>Prevádzkovateľ spracúva osobné údaje len vtedy, ak na ich spracúvanie existuje právny základ</strong> (v súlade so zásadou zákonnosti). Prevádzkovateľ uchováva osobné údaje vždy iba počas doby, počas ktorej je nevyhnutné osobné údaje uchovávať, ktorá je buď určená Prevádzkovateľom v súlade so zásadou minimalizácie alebo vyplýva z ustanovení príslušných právnych predpisov). Po uplynutí tejto doby Prevádzkovateľ osobné údaje vymaže, ak príslušné právne predpisy (napr. v oblasti archivácie) neustanovujú inak.</p>
            <p>Prevádzkovateľ spracúva Vaše osobné údaje iba, ak je to:</p>

            <PrivacySubsection title="Nevyhnutné na plnenie zákonných povinností Prevádzkovateľa (právnym základom je čl. 6 ods. 1. písm. c) Nariadenia).">
              <p>Pri plnení zákonných povinností môžu byť Vaše osobné údaje spracúvané na nasledovné účely:</p>
              <PrivacyPurposeList items={[
                <><strong>Spracovanie účtovných dokladov.</strong> Za týmto účelom spracúvame Vaše osobné údaje v rozsahu: <em>meno, priezvisko, adresa bydliska/miesta podnikania, kontaktné údaje, bankové spojenie, údaje o zrealizovaných nákupoch a iné údaje uvedené na účtovných dokladoch.</em> Vaše osobné údaje budú za týmto účelom spracúvané po dobu 10 rokov nasledujúcich po roku, ktorého sa týkajú;</>,
                <><strong>Správa registratúry, evidencia a vybavovanie prijatej a odoslanej pošty (vrátane elektronickej komunikácie s príslušnými inštitúciami).</strong> Za týmto účelom spracúvame Vaše osobné údaje v rozsahu bežných osobných údajov. Vaše osobné údaje budú za týmto účelom spracúvané po dobu: pošta – 5 rokov nasledujúcich po roku, ktorého sa týkajú; ostatné záznamy tvoriace registratúru v zmysle príslušných ustanovení zákona č. 395/2002 Z. z. o archívoch a registratúrach a o doplnení niektorých zákonov v znení neskorších predpisov.</>,
                <><strong>Vybavovanie reklamácií.</strong> Za týmto účelom spracúvame Vaše osobné údaje v rozsahu <em>bežných osobných údajov.</em> Vaše osobné údaje budú za týmto účelom spracúvané počas 3 rokov odo dňa uplatnenia reklamácie v prípade, ak si reklamáciu uplatňuje fyzická osoba, a 4 roky odo dňa vybavenia reklamácie, ak si reklamáciu uplatní právnická osoba;</>,
                <><strong>Vybavovanie uplatnených práv dotknutých osôb (napr. žiadosť na prístup, odvolanie súhlasu a podobne).</strong> Za týmto účelom spracúvame Vaše osobné údaje v rozsahu: <em>bežných osobných údajov uvedených v žiadosti.</em> Vaše osobné údaje budú za týmto účelom spracúvané do vybavenia uplatnených práv v lehotách ustanovených Nariadením;</>,
                <><strong>Vedenie súdnych a správnych konaní.</strong> Za týmto účelom spracúvame Vaše osobné údaje v rozsahu: bežné osobné údaje potrebné k plneniu zákonných povinností. Vaše osobné údaje budú za týmto účelom spracúvané počas trvania príslušných konaní a do uplynutia premlčacích dôb (ak príslušné právne predpisy neustanovujú inak).</>,
              ]} />
            </PrivacySubsection>

            <PrivacySubsection title="Nevyhnutné na plnenie uzatvorenej zmluvy alebo realizáciu predzmluvných vzťahov (právnym základom je čl. 6 ods. 1. písm. b) Nariadenia).">
              <p>Pri plnení zmluvy a/alebo realizácií predzmluvných vzťahov spracúvame Vaše osobné údaje na nasledujúce účely:</p>
              <PrivacyPurposeList items={[
                <><strong>Plnenie zmluvných povinností s odberateľmi, zákazníkmi, dodávateľmi tovarov a služieb, inými obchodnými partnermi, realizácia predzmluvných vzťahov a zmluvnej komunikácie, plnenie iných zmluvných povinností pri zmluvách uzatvorených s týmito osobami.</strong> Za týmto účelom spracúvame Vaše osobné údaje v rozsahu <em>bežných osobných údajov: meno, priezvisko, obchodné meno, adresa bydliska / miesta podnikania, IČO, dátum narodenia, kontaktné údaje (tel. č., e-mail), bankové spojenie.</em> Vaše osobné údaje budú za týmto účelom spracúvané počas trvania zmluvného vzťahu a po jeho skončení do úplného vyporiadania zmluvných a iných nárokov plynúcich zo zmluvného vzťahu.</>,
              ]} />
            </PrivacySubsection>

            <PrivacySubsection title="Nevyhnutné na účely oprávnených záujmov našej spoločnosti ako Prevádzkovateľa (právnym základom je čl. 6 ods. 1. písm. f) Nariadenia).">
              <p>Na základe tohto právneho základu sú spracúvané Vaše osobné údaje na nasledujúce účely:</p>
              <PrivacyPurposeList items={[
                <><strong>Evidencia uzatvorených zmlúv, dodávateľov, odberateľov, zákazníkov a iných obchodných partnerov (ich kontaktných osôb / zástupcov v prípade, ak sú právnickými osobami), obchodná komunikácia so zástupcami obchodných partnerov a plnenie iných zmluvných povinností pri zmluvách uzatvorených s právnickými osobami.</strong> Za týmto účelom spracúvame Vaše osobné údaje v rozsahu <em>bežných osobných údajov (meno, priezvisko, adresa bydliska / miesta podnikania / fakturačná adresa, príslušnosť / funkcia v spoločnosti, ktorá je objednávateľom, kontaktné údaje – tel. č., e-mail).</em> Vaše osobné údaje spracúvame za týmto účelom počas trvania zmluvného vzťahu a po jeho skončení do uplynutia príslušných premlčacích dôb (na účely vymáhania právnych nárokov vyplývajúcich z uzatvorených zmlúv) alebo do zániku postavenia fyzickej osoby ako zástupcu alebo kontaktnej osoby partnera – právnickej osoby. <strong>SLEDOVANÝ OPRÁVNENÝ ZÁUJEM:</strong> <em>záujem na evidencii zástupcov a kontaktných osôb obchodných partnerov v postavení právnických osôb na účely vedenia účtovníctva, zabezpečenia vnútornej kontrolnej činnosti, plnenia zmluvných povinností voči právnickým osobám a vymáhania právnych a iných nárokov vyplývajúcich z uzatvorených zmlúv;</em></>,
                <><strong>Evidencia uplatnených práv dotknutých osôb a spôsobov, akým boli uplatnené práva vybavené.</strong> Za týmto účelom spracúvame Vaše osobné údaje v rozsahu bežných osobných údajov, ktoré sú súčasťou žiadosti počas 5 rokov odo dňa vybavenia uplatnených práv. <strong>SLEDOVANÝ OPRÁVNENÝ ZÁUJEM:</strong> <em>záujmu prevádzkovateľa, ktorým je potreba evidovania uplatnených práv dotknutých osôb z dôvodu preukázania plnenia povinností vyplývajúcich z príslušných právnych predpisov v oblasti ochrany osobných údajov;</em></>,
                <><strong>Odpovedanie na správy a vybavovanie dotazov / žiadostí zo správ doručených aj prostredníctvom e-mailovej komunikácie alebo telefonicky.</strong> Za týmto účelom spracúvame Vaše osobné údaje v rozsahu bežné osobné údaje - meno, priezvisko, e-mail, tel. č., iné údaje uvedené v správe počas 60 dní odo dňa doručenia žiadosti alebo do vybavenia žiadosti (splnenia účelu), podľa toho, aká skutočnosť nastane skôr. <strong>SLEDOVANÝ OPRÁVNENÝ ZÁUJEM:</strong> <em>odpovedanie na doručené správy pre riadne vedenie obchodnej komunikácie, zlepšovanie kvality poskytovaných služieb a získavanie novej klientely;</em></>,
                <><strong>Spracovanie nevyhnutných funkčných cookies.</strong> Za týmto účelom spracúvame Vaše osobné údaje v rozsahu bežných osobných údajov – IP adresa používaného zariadenia a údaje o používanom webovom prehliadači – po dobu podľa nastavenia jednotlivých súborov cookies (spravidla 3 mesiace od návštevy webovej stránky). <strong>SLEDOVANÝ OPRÁVNENÝ ZÁUJEM:</strong> <em>zabezpečenie funkčnosti webovej stránky.</em></>,
              ]} />
            </PrivacySubsection>

            <PrivacySubsection title="Spracúvanie vykonávané na základe výslovného súhlasu dotknutej osoby (právnym základom je čl. 6 ods. 1. písm. a) Nariadenia).">
              <p>Na základe Vášho súhlasu (ak nám ho udelíte) spracúvame Vaše osobné údaje na nasledujúce účely:</p>
              <PrivacyPurposeList items={[
                <><strong>Meranie návštevnosti webovej stránky, aktivity na webovej stránke a cielenie online reklamy prevádzkovateľa (prostredníctvom online nástroja – cookies).</strong> Za týmto účelom spracúvame Vaše osobné údaje v rozsahu: IP adresa, údaje o aktivite na webovej stránke Prevádzkovateľa a údaje o preferenciách v online prostredí, identifikačné údaje používaného prehliadača, zariadenia, siete a podsiete či údaje o operačnom systéme používaného zariadenia. Vaše osobné údaje budú za týmto účelom spracúvané najdlhšie 2 roky odo dňa udelenia súhlasu alebo do jeho odvolania, podľa toho, ktorá skutočnosť nastane skôr.</>,
              ]} />
            </PrivacySubsection>

            <p>V súvislosti so zabezpečením osobných údajov prijal Prevádzkovateľ príslušnú internú dokumentáciu, v ktorej sú bližšie špecifikované primerané bezpečnostné opatrenia, ktoré boli za účelom zabezpečenia Vašich osobných údajov Prevádzkovateľom prijaté.</p>
          </PrivacySection>

          <PrivacySection title="3. Zdroj osobných údajov">
            <p><strong>Vaše osobné údaje získava Prevádzkovateľ priamo od Vás ako od dotknutej osoby, v prípade, keď mu ich sami poskytnete</strong> (pri poslaní správy cez kontaktný formulár na webovej stránke alebo pri návšteve webovej stránky Prevádzkovateľa). <strong>V niektorých prípadoch, najmä ak si objedná od Prevádzkovateľa službu obchodná spoločnosť alebo iný subjekt, ktorého ste zástupcom alebo kontaktnou osobou,</strong> je zdrojom Vašich osobných údajov práve tento subjekt.</p>
            <p><strong>Ak by ste v niektorých prípadoch neposkytli Vaše osobné údaje Prevádzkovateľovi, nemohol by Vám Prevádzkovateľ dodať službu, uzatvoriť s Vami zmluvu a plniť svoje ostatné zákonné a zmluvné povinnosti, pričom uvedené platí aj pre účely spojené s poskytovaním služby, plnením zákonných či zmluvných povinností Prevádzkovateľa.</strong></p>
          </PrivacySection>

          <PrivacySection title="4. Komu Prevádzkovateľ poskytuje Vaše osobné údaje?">
            <p>Prevádzkovateľ má v určitých prípadoch povinnosť poskytnúť Vaše osobné údaje orgánom verejnej moci, ktoré sú oprávnené v zmysle príslušných právnych predpisov spracúvať Vaše osobné údaje, napr. súdom, orgánom činným v trestnom konaní, príslušnému správcovi dane pri vedení predpísanej účtovnej evidencie a tiež profesijným subjektom ako advokátom, notárom alebo daňovým poradcom (ktorí sú viazaní povinnosťou mlčanlivosti).</p>
            <p>Prevádzkovateľ poskytuje Vaše osobné údaje aj svojím <strong>sprostredkovateľom</strong>, t. j. externým subjektom, ktoré spracúvajú Vaše osobné údaje v mene Prevádzkovateľa. Sprostredkovatelia spracúvajú osobné údaje na základe zmluvy uzavretej s Prevádzkovateľom, v ktorej sa zaviazali, že prijali primerané technické a bezpečnostné opatrenia za účelom bezpečného spracúvania Vašich osobných údajov. K sprostredkovateľom Prevádzkovateľa patria napr.:</p>
            <ul className="list-disc space-y-3 pl-6 marker:text-accent"><li><strong>spoločnosť poskytujúca hostingové služby (vrátane mailhostingových služieb).</strong></li></ul>
            <p>Medzi príjemcov Vašich osobných údajov patria aj spoločnosti ako <strong>Google Ireland Limited</strong>, ktoré poskytujú analytické a marketingové služby prostredníctvom súborov cookies, ktoré do Vášho zariadenia ukladá webová stránka Prevádzkovateľa.</p>
            <p>K príjemcom Vašich osobných údajov patrí aj prevádzkovateľ sociálnych sietí Facebook a Instagram v prípade, ak kontaktujete Prevádzkovateľa prostredníctvom správy na sociálnych sieťach Prevádzkovateľa alebo zdieľate webovú stránku alebo jej obsah na sociálnych sieťach. Vyššie uvedení prevádzkovatelia sociálnych sietí (európske pobočky) vystupujú pri spracúvaní Vašich osobných údajov v postavení samostatných prevádzkovateľov v zmysle dohodnutých podmienok ich používania.</p>
          </PrivacySection>

          <PrivacySection title="5. Prenos do tretích krajín a medzinárodných organizácií, prenos a profilovanie">
            <p>Pri spracúvaní Vašich osobných údajov neprichádza k ich prenosu do tretích krajín alebo medzinárodných organizácií.</p>
            <p>V prípade, ak udelíte súhlas so spracúvaním analytických a marketingových cookie súborov na webovej stránke Prevádzkovateľa, môže prichádzať k prenosu Vašich osobných údajov do USA (napr. spoločnostiam Google Ireland Limited či Meta Platforms, Inc.). Takýto prenos je vždy zabezpečený pomocou primeraných prostriedkov zabezpečenia prenosu osobných údajov do tretích krajín v súlade s Predpismi o ochrane osobných údajov, najmä prostredníctvom používania štandardných zmluvných doložiek, ktoré sú súčasťou podmienok používania vyššie uvedených služieb, prijatého rozhodnutia Európskej Komisie o primeranosti vo vzťahu k USA ako tretej krajine v zmysle príslušných článkov Nariadenia, a tiež prostredníctvom dodatočných záruk prenosu, ktoré poskytovatelia uvedených služieb prijímajú. K prenosu môže prichádzať iba výnimočne, a to na základe príslušných právnych predpisov platných v uvedenej tretej krajine (USA), ktoré sa vzťahujú na uvedených poskytovateľov služieb (FISA).</p>
            <p>Vaše osobné údaje Prevádzkovateľ nespracúva profilovaním, ako ani žiadnou formou automatizovaného individuálneho rozhodovania, pri ktorom by prichádzalo k hodnoteniu Vašich osobných aspektov.</p>
          </PrivacySection>

          <PrivacySection title="6. Aké sú Vaše práva v súvislosti so spracúvaním osobných údajov?">
            <p>V súvislosti so spracúvaním Vašich osobných údajov máte ako dotknutá osoba nasledujúce práva:</p>
            <div className="grid gap-4 md:grid-cols-2">
              <PrivacyRight title="Právo na prístup">Ako dotknutá osoba máte právo získať od Prevádzkovateľa potvrdenie o tom, či spracúva Vaše osobné údaje a ak áno, máte právo získať prístup k týmto osobným údajom a informácie podľa článku 15 Nariadenia. Prevádzkovateľ Vám poskytne kópiu osobných údajov, ktoré sa spracúvajú. Ak podáte žiadosť elektronickými prostriedkami, informácie Vám Prevádzkovateľ poskytne v bežne používanej elektronickej podobe, pokiaľ nepožiadate o iný spôsob.</PrivacyRight>
              <PrivacyRight title="Právo na opravu">Na zabezpečenie presnosti, úplnosti a aktuálnosti Vašich osobných údajov prijal Prevádzkovateľ primerané opatrenia. Ako dotknutá osoba máte právo na to, aby Prevádzkovateľ bez zbytočného odkladu opravil Vaše nesprávne osobné údaje alebo doplnil Vaše neúplné osobné údaje.</PrivacyRight>
              <PrivacyRight title="Právo na vymazanie (právo „na zabudnutie“) ">Taktiež máte právo dosiahnuť u Prevádzkovateľa bez zbytočného odkladu vymazanie Vašich osobných údajov, ak sú splnené určité podmienky, napríklad v prípade, ak osobné údaje už nie sú potrebné na účely, na ktoré ich Prevádzkovateľ získal alebo spracúval. Toto Vaše právo je však potrebné posudzovať individuálne, keďže môže nastať situácia, kedy Prevádzkovateľovi vo výmaze osobných údajov bránia iné okolnosti (napríklad zákonná povinnosť Prevádzkovateľa). To znamená, že v takom prípade nebude môcť Prevádzkovateľ vyhovieť Vašej žiadosti o vymazanie osobných údajov.</PrivacyRight>
              <PrivacyRight title="Právo na obmedzenie spracúvania">Taktiež máte právo na to, aby Prevádzkovateľ obmedzil spracúvanie Vašich osobných údajov. Bude tomu tak napríklad v prípade, ak napadnete správnosť osobných údajov alebo ak je spracúvanie protizákonné a žiadate o obmedzenie spracúvania, prípadne ak Prevádzkovateľ už nepotrebuje Vaše osobné údaje na účely spracúvania, ale potrebujete ich Vy na preukázanie, uplatňovanie alebo obhajovanie právnych nárokov. Prevádzkovateľ obmedzí spracúvanie Vašich osobných údajov, ak o to požiadate.</PrivacyRight>
              <PrivacyRight title="Právo na prenosnosť údajov">Za určitých okolností máte právo na prenos osobných údajov inému prevádzkovateľovi, ktorého určíte. Právo na prenosnosť sa však týka len osobných údajov, ktoré Prevádzkovateľ spracúva na základe súhlasu, ktorý ste Prevádzkovateľovi udelili, na základe zmluvy, ktorej ste jednou zo zmluvných strán alebo v prípade, ak Prevádzkovateľ spracúva osobné údaje automatizovanými prostriedkami.</PrivacyRight>
              <PrivacyRight title="Právo podať sťažnosť alebo podnet">V prípade, ak máte pocit, že sú Vaše osobné údaje spracúvané v rozpore s platnými právnymi predpismi, môžete sa so sťažnosťou obrátiť na dozorný orgán, ktorým je Úrad na ochranu osobných údajov Slovenskej republiky, so sídlom Galvaniho Business Centrum II, Galvaniho 7/B, Bratislava; webové sídlo: <a className="underline decoration-accent underline-offset-4" href="https://dataprotection.gov.sk/sk" target="_blank" rel="noreferrer">dataprotection.gov.sk/sk</a>, tel. číslo: 02 3231 3214; e-mail: <a className="underline decoration-accent underline-offset-4" href="mailto:statny.dozor@pdp.gov.sk">statny.dozor@pdp.gov.sk</a>.</PrivacyRight>
              <PrivacyRight title="Právo namietať">Máte právo namietať voči spracúvaniu osobných údajov, napríklad v prípade, ak Prevádzkovateľ spracúva Vaše osobné údaje na základe oprávneného záujmu alebo pri spracúvaní, v rámci ktorého dochádza k profilovaniu. Ak podáte námietku voči takémuto spracúvaniu osobných údajov, Prevádzkovateľ nebude Vaše osobné údaje ďalej spracúvať, ak nepreukáže nevyhnutné oprávnené dôvody na ďalšie spracúvanie Vašich osobných údajov.</PrivacyRight>
              <PrivacyRight title="Právo odvolať súhlas">Ak Prevádzkovateľ spracúva Vaše osobné údaje na základe Vášho súhlasu, máte právo udelený súhlas kedykoľvek odvolať rovnakou formou, ako ste ho udelili. Odvolanie súhlasu nemá vplyv na zákonnosť spracúvania vykonaného pred odvolaním súhlasu.</PrivacyRight>
            </div>
            <p>Vaše práva špecifikované v tabuľke vyššie si môžete uplatniť na kontaktných adresách Prevádzkovateľa uvedených v úvode tohto dokumentu. Odpoveď na uplatnenie Vašich práv Vám poskytne Prevádzkovateľ bezplatne. V prípade opakovanej, nedôvodnej alebo neprimeranej žiadosti o uplatnenie Vašich práv je Prevádzkovateľ oprávnený účtovať za poskytnutie informácií primeraný poplatok. Odpoveď Vám Prevádzkovateľ poskytne <strong>do 1 mesiaca</strong> odo dňa, kedy ste si uplatnili Vaše práva. V určitých prípadoch je Prevádzkovateľ oprávnený túto lehotu predĺžiť, a to v prípade vysokého počtu a komplexnosti žiadostí dotknutých osôb, najviac však o 2 mesiace. O predĺžení lehoty Vás bude Prevádzkovateľ vždy informovať.</p>
          </PrivacySection>

          <PrivacySection title="7. Ako môžete odvolať súhlas so spracovaním osobných údajov?">
            <p>V prípade, ak sú Vaše osobné údaje spracúvané na základe Vášho súhlasu udeleného Prevádzkovateľovi, je súhlas so spracúvaním osobných údajov založený na princípe dobrovoľnosti. To znamená, že ho môžete kedykoľvek odvolať.</p>
            <p><strong>Pri odvolaní súhlasu uveďte svoje meno a priezvisko a adresu bydliska, aby sme Vás mohli identifikovať.</strong> Uveďte tiež informáciu, že si neželáte, aby sme spracovávali Vaše osobné údaje. Odvolanie súhlasu so spracovaním osobných údajov môžete zaslať písomne alebo prostredníctvom našej kontaktnej e-mailovej adresy <a className="underline decoration-accent underline-offset-4" href="mailto:info@imautotech.sk">info@imautotech.sk</a>.</p>
          </PrivacySection>

          <PrivacySection title="8. Sociálne médiá a odkazy na iné internetové stránky">
            <p>V rámci podpory marketingu a reklamy nájdete na webovej stránke Prevádzkovateľa odkazy na rôzne sociálne siete, ako je napr. Facebook alebo Instagram. Prevádzkovateľ si Vás týmto dovoľuje upozorniť, že po kliknutí na doplnok na internetovej stránke a prechode na sociálnu sieť začnú platiť pravidlá ochrany osobných údajov prevádzkovateľa sociálnej siete, okrem prípadov, kedy kontaktujete Prevádzkovateľa prostredníctvom správy na sociálnej sieti (v takom prípade sa spracúvanie Vašich osobných údajov riadi aj týmito Zásadami a Vaše osobné údaje spracúvané Prevádzkovateľom v súlade s informáciami uvedenými vyššie).</p>
            <p>Bližšie informácie o spracúvaní Vašich osobných údajov prevádzkovateľmi sociálnych sietí nájdete na nasledujúcich odkazoch: (i) Facebook a (ii) Instagram.</p>
          </PrivacySection>

          <PrivacySection title="9. Platnosť">
            <p>Tieto aktualizované Zásady sú platné a účinné od 01.07.2026. Vzhľadom na to, že sa môže v budúcnosti vyžadovať aktualizácia informácií o spracúvaní osobných údajov obsiahnutých v týchto Zásadách, Prevádzkovateľ je oprávnený tieto Zásady kedykoľvek aktualizovať. V takom prípade Vás však o tom Prevádzkovateľ náležitým spôsobom vopred oboznámi.</p>
          </PrivacySection>
        </div>
      </div>
    </section>
  );
}

function PrivacySection({ title, children }: { title: string; children: ReactNode }) {
  const headingId = `privacy-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;

  return (
    <section className="scroll-mt-24 space-y-6" aria-labelledby={headingId}>
      <h2 id={headingId} className="border-b border-border pb-4 text-2xl font-extrabold tracking-[.01em] text-foreground sm:text-3xl">{title}</h2>
      {children}
    </section>
  );
}

function PrivacySubsection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-5 border-l-2 border-accent/40 pl-5 sm:pl-6">
      <h3 className="text-lg font-bold leading-7 text-foreground">{title}</h3>
      {children}
    </div>
  );
}

function PrivacyPurposeList({ items }: { items: ReactNode[] }) {
  return <ul className="list-disc space-y-5 pl-6 marker:text-accent">{Children.toArray(items).map((item) => <li key={isValidElement(item) ? item.key : String(item)}>{item}</li>)}</ul>;
}

function PrivacyRight({ title, children }: { title: string; children: ReactNode }) {
  return (
    <article className="rounded-sm border border-border/70 bg-muted/30 p-5 sm:p-6">
      <h3 className="text-lg font-bold leading-7 text-foreground">{title}</h3>
      <p className="mt-3 text-sm leading-7">{children}</p>
    </article>
  );
}

function CookiesPage() {
  return (
    <section className="py-20 sm:py-28">
      <div className="container max-w-3xl">
        <h1 className="mb-12 text-4xl font-extrabold tracking-[.01em] sm:text-6xl">Používanie cookies</h1>
        <CookieDeclaration />
      </div>
    </section>
  );
}

function CookieDeclaration() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const script = document.createElement("script");
    script.id = "CookieDeclaration";
    script.src = "https://consent.cookiebot.com/1bfce3bd-1c08-4133-b740-cbdd0df55b6d/cd.js";
    script.type = "text/javascript";
    script.async = true;
    container.appendChild(script);

    return () => container.replaceChildren();
  }, []);

  return <div ref={containerRef} data-cookie-declaration />;
}

function TextLink({ href: target, children }: { href: string; children: ReactNode }) {
  return <Button asChild variant="accent" size="lg" className="mt-8"><a href={target}>{children}<ArrowUpRight data-icon="inline-end" /></a></Button>;
}

const RESPONSIVE_IMAGE_WIDTHS = [480, 800, 1200, 1600, 2200];

function ResponsiveImage({ src, sizes = "100vw", ...props }: { src: string; sizes?: string } & ImgHTMLAttributes<HTMLImageElement>) {
  const isUnsplash = src.startsWith("https://images.unsplash.com/");
  if (!isUnsplash) return <img src={src} sizes={sizes} {...props} />;

  const imageUrl = (width: number, format?: "avif" | "webp") => {
    const url = new URL(src);
    url.searchParams.set("w", String(width));
    if (format) url.searchParams.set("fm", format);
    return url.toString();
  };
  const srcSet = (format?: "avif" | "webp") => RESPONSIVE_IMAGE_WIDTHS.map((width) => `${imageUrl(width, format)} ${width}w`).join(", ");

  return (
    <picture>
      <source type="image/avif" srcSet={srcSet("avif")} sizes={sizes} />
      <source type="image/webp" srcSet={srcSet("webp")} sizes={sizes} />
      <img src={imageUrl(1600)} srcSet={srcSet()} sizes={sizes} {...props} />
    </picture>
  );
}

function useHeaderShadow() {
  const [hasScrolled, setHasScrolled] = useState(false);
  useEffect(() => {
    const update = () => setHasScrolled(window.scrollY > 8);
    update();
    window.addEventListener("scroll", update, { passive: true });
    return () => window.removeEventListener("scroll", update);
  }, []);
  return hasScrolled;
}

function SiteFooter({ copy }: { copy: Copy }) {
  return (
    <footer className="bg-primary py-12 text-primary-foreground">
      <div className="container">
        <Separator className="bg-white/25" />
        <div className="grid gap-12 py-12 lg:grid-cols-[1.3fr_1fr_1.1fr_.8fr] lg:gap-10">
          <div className="max-w-sm">
            <img src={whiteLogo} alt={copy.logoAlt} width="220" height="44" className="h-8 w-auto" />
            <p className="mt-5 text-sm leading-6 text-white/60">{copy.footer}</p>
            <Button asChild variant="accent" size="lg" className="mt-8">
              <a href={href("contact")}>{copy.labels.book}<ArrowUpRight data-icon="inline-end" aria-hidden="true" /></a>
            </Button>
            <div className="mt-8 text-sm leading-6 text-white/55">
              <p className="font-semibold text-white/80">{copy.company.title}</p>
              <p>{copy.company.name}</p>
              <p>IČO: {copy.company.ico}</p>
              <p>DIČ: {copy.company.dic}</p>
              <p>{copy.company.address}</p>
            </div>
          </div>

          <div>
            <p className="text-sm font-semibold tracking-[.16em] text-white/65 uppercase">{copy.labels.services}</p>
            <nav aria-label={copy.labels.services} className="mt-5 flex flex-col gap-3">
              {copy.services.items.map((serviceItem) => (
                <a key={serviceItem.slug} href={serviceHref(serviceItem.slug)} className={`block ${footerLinkClassName}`}>
                  {serviceItem.title}
                </a>
              ))}
            </nav>
          </div>

          <div>
            <p className="text-sm font-semibold tracking-[.16em] text-white/65 uppercase">{copy.labels.contact}</p>
            <nav aria-label={copy.labels.contact} className="mt-5 flex flex-col gap-3">
              {copy.contact.phoneValues.map((phone) => <a key={phone.href} className={`block ${footerLinkClassName}`} href={phone.href}>{phone.value}</a>)}
              <a className={`block ${footerLinkClassName}`} href={`mailto:${copy.contact.email}`}>{copy.contact.email}</a>
              <a className={`block ${footerLinkClassName}`} href="https://maps.app.goo.gl/k6JvXCn5b1G5m52y7" target="_blank" rel="noreferrer">{copy.contact.address}</a>
            </nav>
          </div>

          <div>
            <p className="text-sm font-semibold tracking-[.16em] text-white/65 uppercase">Menu</p>
            <nav aria-label="Menu" className="mt-5 flex flex-col gap-3">
              {navItems.map((item) => (
                <a key={item} href={href(item)} className={`block ${footerLinkClassName}`}>{copy.labels[item]}</a>
              ))}
            </nav>
          </div>
        </div>

        <div className="flex flex-col gap-4 border-t border-white/25 pt-6 text-sm text-white/60 sm:flex-row sm:items-center sm:justify-between">
          <p>© 2026 IM Autotech. Všetky práva vyhradené.</p>
          <nav aria-label="Právne informácie" className="flex flex-wrap items-center gap-x-6 gap-y-2 sm:justify-end">
            <a className={footerLinkClassName} href={href("privacy")}>Ochrana osobných údajov</a>
            <a className={footerLinkClassName} href={href("cookies")}>Cookies</a>
          </nav>
        </div>
      </div>
    </footer>
  );
}
